// src/components/BESSForm.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import { useHistory } from "../context/HistoryContext";
import { useIsGreek, useTranslateText } from "../context/LanguageContext";
import {
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Divider,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  previewSurfaceSx,
  readOnlyFieldSx,
  getPreviewAccents,
  getChartChrome,
  getRechartsTooltipProps,
} from "../theme";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Autocomplete from "@mui/material/Autocomplete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LabeledSwitchWithHint from "../components/LabeledSwitchWithHint";
import AddIcon from "@mui/icons-material/Add";
import Snackbar from "@mui/material/Snackbar";
import { createBattery, getCatalogOrganizations } from "../services/api";
import CatalogVisibilityFields from "./catalog/CatalogVisibilityFields";
import { useAuth } from "../context/AuthContext";
import Checkbox from "@mui/material/Checkbox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { DEFAULT_CONFIG } from "./config/defaultConfig";
import { deleteBattery, getBatteries, getBatteryDetails } from "../services/api";
import MaybeTooltip from "../components/MaybeTooltip";
import { useCurrentUserAccess } from "../hooks/useCurrentUserAccess";
import { formatApiError } from "../utils/apiErrors";
import SetupPageHeader from "../components/SetupPageHeader";
import DispatchWindowsSection from "../components/DispatchWindowsSection";
import BtmWearCostField from "../components/BtmWearCostField";
import { normalizeRegulatoryFramework } from "./config/regulatoryFramework";
import {
  availabilityModellingIssue,
  milpAvailabilityFactor,
} from "./config/availabilityModelling";
import {
  isBtmSettlement,
  normalizeBtm,
  btmGridChargingState,
  btmGridInjectionState,
  btmBatteryInjectionState,
  btmSimultaneousGridPvState,
  GRID_INJECTION_SWITCH_LABEL,
  BATTERY_INJECTION_SWITCH_LABEL,
  BTM_INJECT_DRAW_HINT,
  BTM_INJECT_DRAW_CAPTION,
  STANDALONE_GRID_CHARGING_HINT,
  STANDALONE_GRID_CHARGING_CAPTION,
  WEAR_COST_HELPER_TEXT,
} from "./config/btm";
import {
  forwardRedispatchYears,
  unifiedRedispatchYears,
} from "./config/merchantRevenueProjection";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

const DEFAULT_BATTERY_OVERRIDES = {
  iniSoC: 50,
  startDaySoC: 70,
  dailyCycles: 1,
  minIdleMins: 0,
  eol_soh_pct: 80,
};

// same helper as in ConfigForm
function setPathInObject(obj, path, value) {
  const keys = path.split(".");
  const clone = { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = { ...(cur[k] ?? {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}
function extractBatterySpecs(details) {
  if (!details) return {};

  const chars = details.characteristics || {};

  const energy = details.energy ?? chars.Energy;
  const pMaxC = details.p_max_c ?? chars.P_max_c;
  const pMaxD = details.p_max_d ?? chars.P_max_d;
  const tech = formatBatteryTechnology(chars.Technology || chars.Tech || "");
  const vNom = chars.Vnom || chars.V_nom || chars.V_nominal;
  const vMin = chars.Vmin || chars.V_min;
  const vMax = chars.Vmax || chars.V_max;
  const effC = chars.Eff_charge;
  const effD = chars.Eff_discharge;
  const c_rate_c = chars["c-rate_c"];
  const c_rate_d = chars["c-rate_d"];
  const width = chars.Width;
  const length = chars.Length;
  const height = chars.Height;
  const weight = chars.Weight;
  const i_d_max = chars.I_disch_max;
  const soc_min = chars.soc_min;
  const soc_max = chars.soc_max;
  const price = chars.Price;
  const cycles = chars.N_cycles;

  return {
    energy,
    pMaxC,
    pMaxD,
    tech,
    vNom,
    vMin,
    vMax,
    effC,
    effD,
    c_rate_c,
    c_rate_d,
    width,
    length,
    height,
    weight,
    i_d_max,
    soc_min,
    soc_max,
    cycles,
    price,
  };
}

const BATTERY_TECHNOLOGY_OPTIONS = [
  { value: "leadacid", label: "Lead-acid" },
  { value: "lfpgraphite", label: "LFP / Graphite" },
  { value: "nmcgraphite", label: "NMC / Graphite" },
  { value: "lmolto", label: "LMO / LTO" },
];

function formatBatteryTechnology(value) {
  const match = BATTERY_TECHNOLOGY_OPTIONS.find((option) => option.value === value);
  return match?.label || value || "—";
}

function pickFirstFiniteValue(values) {
  for (const value of values) {
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return null;
}


// --------- small component to show the “drawing” + specs in the dialog ----------
// --------- small component to show the “drawing” + specs in the dialog ----------
function BatteryDiagram({ details }) {
  if (!details) return null;

  const {
    energy,
    pMaxC,
    pMaxD,
    tech,
    vNom,
    vMin,
    vMax,
    effC,
    effD,
    c_rate_c,
    c_rate_d,
    width,
    length,
    height,
    weight,
    i_d_max,
    soc_min,
    soc_max,
    cycles,
    price,
  } = extractBatterySpecs(details);

  const fmtPercent = (x) => {
    if (x === null || x === undefined) return "—";
    const v = x <= 1 ? x * 100 : x;
    return `${v.toFixed(2)} %`;
  };

  const fmtOrDash = (x, suffix = "") =>
    x === null || x === undefined || x === "" ? "—" : `${x}${suffix}`;

  const Section = ({ title, children }) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontSize: "0.78rem",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          color: "text.secondary",
          pb: 0.5,
          mb: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {title}
      </Typography>
      <Grid container spacing={1.5}>{children}</Grid>
    </Box>
  );

  const Spec = ({ label, value }) => (
    <Grid size={6}>
      <Typography variant="caption" color="text.primary">
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Grid>
  );

  return (
    <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start", flexWrap: { xs: "wrap", sm: "nowrap" } }}>
      {/* Left: battery “card” */}
      <Box
        sx={{
          width: 200,
          flexShrink: 0,
          borderRadius: 3,
          boxShadow: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: "primary.main",
            color: "primary.contrastText",
            p: 1.2,
          }}
        >
          <Typography
            variant="caption"
            sx={{ textTransform: "uppercase", letterSpacing: 0.6 }}
          >
            {tech}
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 0.3 }}>
            {details.name}
          </Typography>
        </Box>

        {/* Body */}
        <Box
          sx={{
            p: 1.75,
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Energy
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {energy ? `${energy} kWh` : "—"}
            </Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="caption" color="text.secondary">
              Price
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              {price ? `${price.toLocaleString("en-US")} €` : "—"}
            </Typography>
          </Box>
          <Divider />
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Charge
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {fmtOrDash(pMaxC, " kW")}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Discharge
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {fmtOrDash(pMaxD, " kW")}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Right: specs grouped in sections */}
      <Box sx={{ flex: 1 }}>
        <Section title="Electrical">
          <Spec label="Nominal voltage" value={fmtOrDash(vNom, " V")} />
          <Spec
            label="Voltage range"
            value={
              vMin && vMax ? `${vMin} – ${vMax} V` : "—"
            }
          />
          <Spec label="I_discharge max" value={fmtOrDash(i_d_max, " A")} />
          <Spec
            label="c-rate charge"
            value={fmtOrDash(c_rate_c, "")}
          />
          <Spec
            label="c-rate discharge"
            value={fmtOrDash(c_rate_d, "")}
          />
        </Section>

        <Section title="Operating limits">
          <Spec label="Eff. charge" value={fmtPercent(effC)} />
          <Spec label="Eff. discharge" value={fmtPercent(effD)} />
          <Spec label="Min SoC" value={fmtOrDash(soc_min, " %")} />
          <Spec label="Max SoC" value={fmtOrDash(soc_max, " %")} />
          <Spec
            label="Cycle life"
            value={
              cycles ? `${cycles.toLocaleString("en-GB")} cycles` : "—"
            }
          />
        </Section>

        <Section title="Physical">
          <Spec label="Width" value={fmtOrDash(width, " m")} />
          <Spec label="Height" value={fmtOrDash(height, " m")} />
          <Spec label="Length" value={fmtOrDash(length, " m")} />
          <Spec label="Weight" value={fmtOrDash(weight, " kg")} />
        </Section>
      </Box>
    </Box>
  );
}

function formatCatalogUploadSuccessMessage(kind, response, fallbackName) {
  const action = String(response?.action || "saved").trim().toLowerCase();
  const actionLabel = action ? action.charAt(0).toUpperCase() + action.slice(1) : "Saved";
  const itemName = String(response?.name || fallbackName || "").trim();
  return itemName
    ? `${kind} "${itemName}" ${actionLabel.toLowerCase()} successfully.`
    : `${kind} ${actionLabel.toLowerCase()} successfully.`;
}


/* Shared visual building block for the form: a titled card that keeps a group
   of settings, its explanation and its fields together — instead of captions
   floating between unrelated inputs. `action` renders on the header's right
   (e.g. the "Add custom battery" button lives next to the selection title). */
function SubSection({ title, description, action, children, sx }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        width: "100%",
        ...sx,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          mb: description ? 0.25 : 1.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: 0.6,
            fontSize: "0.78rem",
            color: "text.secondary",
            pt: 0.5,
          }}
        >
          {title}
        </Typography>
        {action || null}
      </Box>
      {description ? (
        <Typography
          sx={{
            // One readable size for every explanatory line in this form; the
            // 0.75rem caption default was too small for paragraph text.
            fontSize: "0.845rem",
            lineHeight: 1.55,
            color: "text.secondary",
            display: "block",
            mb: 1.5,
            maxWidth: 1040,
          }}
        >
          {description}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

/* ---------- OEM retention curve editor ----------
   Friendly table editor over the SAME config string the backend parses
   ("year:retained %, ..."), so nothing changes downstream. Incomplete rows
   live only in local state; only valid anchors are serialized. A text mode
   remains for pasting a whole table at once. */
function parseRetentionRows(raw) {
  const rows = [];
  String(raw || "")
    .replace(/;/g, ",")
    .split(",")
    .forEach((tok) => {
      const parts = tok.split(":");
      if (parts.length !== 2) return;
      const year = parts[0].trim();
      const pct = parts[1].trim();
      if (year === "" && pct === "") return;
      rows.push({ year, pct });
    });
  return rows;
}

function serializeRetentionRows(rows) {
  return rows
    .filter((r) => {
      const y = Number(r.year);
      const p = Number(r.pct);
      return Number.isFinite(y) && y >= 1 && Number.isFinite(p) && p > 0;
    })
    .map((r) => `${Math.round(Number(r.year))}:${r.pct}`)
    .join(", ");
}

/* Anchors loaded when the warranty-curve mode is picked and no table exists
   yet, so the mode is usable the moment it is selected instead of warning and
   quietly falling back somewhere else. Same rule as the converter's typical
   curve: these values reproduce the path the study would have taken anyway -
   2%/yr geometric, retention (1 - 0.02)^(year - 1) - to within 0.15 percentage
   points at every year, so picking the mode changes the wording and not the
   result. They are a typical LFP shape, NOT manufacturer data: the panel says
   so, and `retentionCurveSource` keeps the report's provenance honest until a
   real table replaces them. */
const TYPICAL_RETENTION_POINTS = [
  [1, 100.0], [2, 98.0], [5, 92.2], [10, 83.4], [15, 75.4], [20, 68.1], [25, 61.6],
];
const TYPICAL_RETENTION_CURVE = TYPICAL_RETENTION_POINTS
  .map(([y, p]) => `${y}:${p}`)
  .join(", ");

function RetentionCurveEditor({ value, onChange, disabled }) {
  const [textMode, setTextMode] = useState(false);
  const [rows, setRows] = useState(() => parseRetentionRows(value));
  const lastEmitted = useRef(value || "");

  useEffect(() => {
    // Adopt EXTERNAL changes (project load, undo) we did not emit ourselves -
    // without clobbering half-typed rows on our own round trips.
    if ((value || "") !== lastEmitted.current) {
      setRows(parseRetentionRows(value));
      lastEmitted.current = value || "";
    }
  }, [value]);

  const emit = (nextRows) => {
    setRows(nextRows);
    const serialized = serializeRetentionRows(nextRows);
    lastEmitted.current = serialized;
    onChange(serialized);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: disabled ? "text.disabled" : "text.secondary" }}
        >
          OEM Retention Curve
        </Typography>
        <Button
          size="small"
          disabled={disabled}
          onClick={() => setTextMode((prev) => !prev)}
          sx={{ textTransform: "none", fontSize: "0.72rem", py: 0, minWidth: 0 }}
        >
          {textMode ? "Edit as table" : "Paste as text"}
        </Button>
      </Box>
      {textMode ? (
        <TextField
          fullWidth
          size="small"
          value={value ?? ""}
          disabled={disabled}
          onChange={(e) => {
            lastEmitted.current = e.target.value;
            setRows(parseRetentionRows(e.target.value));
            onChange(e.target.value);
          }}
          placeholder="1:97, 5:91, 10:85"
          helperText="year:retained %, comma-separated"
          sx={{ mt: 0.5 }}
        />
      ) : (
        <>
          <Stack spacing={0.75} sx={{ mt: 0.75 }}>
            {rows.length === 0 ? (
              <Typography
                variant="caption"
                sx={{ color: disabled ? "text.disabled" : "text.secondary" }}
              >
                {disabled
                  ? "Available with the 'OEM warranty curve' degradation mode."
                  : "Add the anchor years from the manufacturer's warranty table."}
              </Typography>
            ) : (
              /* One header row, not a floating label on every one of the 2N
                 cells. This is a two-column table; at seven anchors the
                 repeated "Year" / "Retained %" labels were most of the ink on
                 the panel and each row carried its own outlined box twice. */
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                <Typography
                  variant="caption"
                  sx={{ width: 92, fontWeight: 600,
                        color: disabled ? "text.disabled" : "text.secondary" }}
                >
                  Year
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ width: 124, fontWeight: 600,
                        color: disabled ? "text.disabled" : "text.secondary" }}
                >
                  Retained %
                </Typography>
                <Box sx={{ width: 34, flexShrink: 0 }} />
              </Box>
            )}
            {rows.map((row, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 1, step: 1, "aria-label": `Anchor year ${index + 1}` }}
                  value={row.year}
                  disabled={disabled}
                  onChange={(e) =>
                    emit(rows.map((r, k) => (k === index ? { ...r, year: e.target.value } : r)))
                  }
                  sx={{ width: 92 }}
                />
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ min: 1, max: 100, step: 0.5,
                                "aria-label": `Retained percent at anchor ${index + 1}` }}
                  value={row.pct}
                  disabled={disabled}
                  onChange={(e) =>
                    emit(rows.map((r, k) => (k === index ? { ...r, pct: e.target.value } : r)))
                  }
                  sx={{ width: 124 }}
                />
                <MaybeTooltip title="Remove this anchor" placement="top" arrow>
                  <span>
                    <IconButton
                      size="small"
                      disabled={disabled}
                      onClick={() => emit(rows.filter((_, k) => k !== index))}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </span>
                </MaybeTooltip>
              </Box>
            ))}
          </Stack>
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={disabled}
            onClick={() => emit([...rows, { year: "", pct: "" }])}
            sx={{ mt: 0.5, textTransform: "none" }}
          >
            Add anchor year
          </Button>
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
            Linear between the anchors, and the last slope carries on past the final one.
          </Typography>
        </>
      )}
    </Box>
  );
}

/* Splits a pasted table into [temp, value] pairs. Accepts the platform's own
   "-10:94, 0:97" form and, so a datasheet table can be pasted straight in,
   rows separated by new lines with the two columns split by a tab, a space
   or a semicolon. */
function tokenizeTempCurve(raw) {
  return String(raw || "")
    .replace(/[;\n\r]+/g, ",")
    .split(",")
    .map((tok) => tok.trim())
    .filter(Boolean)
    .map((tok) => {
      const parts = tok.includes(":") ? tok.split(":") : tok.split(/[\s\t]+/);
      if (parts.length !== 2) return null;
      return [parts[0].trim(), parts[1].trim()];
    })
    .filter((pair) => pair && !(pair[0] === "" && pair[1] === ""));
}

function parseTempCurveRows(raw) {
  return tokenizeTempCurve(raw).map(([temp, pct]) => ({ temp, pct }));
}

function serializeTempCurveRows(rows) {
  return rows
    .filter((r) => {
      const t = Number(r.temp);
      const p = Number(r.pct);
      return Number.isFinite(t) && t >= -60 && t <= 90 && Number.isFinite(p) && p > 0 && p <= 100;
    })
    .map((r) => `${r.temp}:${r.pct}`)
    .join(", ");
}

function TempCurveEditor({ value, onChange }) {
  const [textMode, setTextMode] = useState(false);
  const [rows, setRows] = useState(() => parseTempCurveRows(value));
  const lastEmitted = useRef(value || "");

  useEffect(() => {
    // Adopt EXTERNAL changes (project load, undo) we did not emit ourselves -
    // without clobbering half-typed rows on our own round trips.
    if ((value || "") !== lastEmitted.current) {
      setRows(parseTempCurveRows(value));
      lastEmitted.current = value || "";
    }
  }, [value]);

  const emit = (nextRows) => {
    setRows(nextRows);
    const serialized = serializeTempCurveRows(nextRows);
    lastEmitted.current = serialized;
    onChange(serialized);
  };

  const isCustom = rows.length > 0;
  const loadStandard = () =>
    emit(TEMP_EFF_POINTS.map(([t, f]) => ({ temp: String(t), pct: String(+(f * 100).toFixed(1)) })));

  return (
    <Box sx={{ mt: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
          Efficiency vs Temperature —{" "}
          {isCustom ? `your curve, ${rows.length} point${rows.length === 1 ? "" : "s"}` : "standard curve"}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
          <Button
            size="small"
            onClick={isCustom ? () => emit([]) : loadStandard}
            sx={{ textTransform: "none", fontSize: "0.72rem", py: 0, minWidth: 0 }}
          >
            {isCustom ? "Back to standard" : "Edit the standard values"}
          </Button>
          <Button
            size="small"
            onClick={() => setTextMode((prev) => !prev)}
            sx={{ textTransform: "none", fontSize: "0.72rem", py: 0, minWidth: 0 }}
          >
            {textMode ? "Edit as table" : "Paste a table"}
          </Button>
        </Box>
      </Box>
      {textMode ? (
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={3}
          value={value ?? ""}
          onChange={(e) => {
            lastEmitted.current = e.target.value;
            setRows(parseTempCurveRows(e.target.value));
            onChange(e.target.value);
          }}
          placeholder={"-10:94, 0:97, 25:100, 50:98\nor two columns pasted from the datasheet"}
          helperText="One point per line or separated by commas: temperature °C then efficiency %. Pasting two columns from a spreadsheet works as well."
          sx={{ mt: 0.5 }}
        />
      ) : (
        <>
          {!isCustom && (
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.75 }}>
              Load the standard values to adjust them, paste a table, or build your own
              point by point.
            </Typography>
          )}
          {/* Scrolls once the list is long instead of pushing the chart and
              everything under it down the page. A datasheet table can run to a
              dozen points; the panel should not grow with it. */}
          <Stack
            spacing={0.75}
            sx={{
              mt: 0.75,
              ...(rows.length > 4 && {
                maxHeight: 232,
                overflowY: "auto",
                pr: 0.5,
                borderLeft: "2px solid",
                borderColor: "divider",
                pl: 1,
              }),
            }}
          >
            {rows.map((row, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  size="small"
                  type="number"
                  label="Temp (°C)"
                  value={row.temp}
                  onChange={(e) =>
                    emit(rows.map((r, k) => (k === index ? { ...r, temp: e.target.value } : r)))
                  }
                  inputProps={{ min: -60, max: 90, step: 1 }}
                  sx={{ width: 104 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Efficiency %"
                  value={row.pct}
                  onChange={(e) =>
                    emit(rows.map((r, k) => (k === index ? { ...r, pct: e.target.value } : r)))
                  }
                  inputProps={{ min: 50, max: 100, step: 0.5 }}
                  sx={{ width: 128 }}
                />
                <MaybeTooltip title="Remove this point" placement="top" arrow>
                  <span>
                    <IconButton size="small" onClick={() => emit(rows.filter((_, k) => k !== index))}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </span>
                </MaybeTooltip>
              </Box>
            ))}
          </Stack>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 0.5 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => emit([...rows, { temp: "", pct: "" }])}
              sx={{ textTransform: "none" }}
            >
              Add point
            </Button>
            {!isCustom && (
              <Button size="small" onClick={loadStandard} sx={{ textTransform: "none" }}>
                Load the standard values
              </Button>
            )}
          </Box>
          {/* One sentence, not three. The paragraph that used to sit here
              repeated the standard-curve figures already stated above it and
              again under the chart. */}
          <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
            Efficiency retained at each ambient temperature, 100% meaning no effect.
            Interpolated between points, flat beyond the ends.
            {isCustom ? " Remove every point to return to the standard curve." : ""}
          </Typography>
        </>
      )}
    </Box>
  );
}

/* ---------- Converter efficiency curve (datasheet) ----------
   Same storage contract as the engine: "load %:efficiency %" pairs, which
   Configuration/battery_performance.py fits into the fixed and proportional
   loss terms. The fit here mirrors that least-squares solve exactly, so the
   read-back beside the table is the same pair of numbers the run will use. */
function parsePcsCurveRows(raw) {
  return tokenizeTempCurve(raw).map(([load, pct]) => ({ load, pct }));
}

function serializePcsCurveRows(rows) {
  return rows
    .filter((r) => {
      const l = Number(r.load);
      const e = Number(r.pct);
      return Number.isFinite(l) && l > 0 && l <= 100 && Number.isFinite(e) && e > 0 && e <= 100;
    })
    .map((r) => `${r.load}:${r.pct}`)
    .join(", ");
}

function parsePcsCurvePoints(raw) {
  const map = new Map();
  tokenizeTempCurve(raw).forEach(([rawL, rawE]) => {
    const l = Number(rawL);
    let e = Number(rawE);
    if (!Number.isFinite(l) || !Number.isFinite(e)) return;
    if (e <= 1.5) e *= 100;
    if (l > 0 && l <= 100 && e > 0 && e <= 100) map.set(l, e);
  });
  const pts = [...map.entries()].sort((a, b) => a[0] - b[0]);
  return pts.length >= 2 ? pts : null;
}

/* Least-squares fit of loss = k0 + k1·P over the datasheet points — the same
   solve as fit_pcs_affine_params, including the clamps. */
function fitPcsFromCurve(points, ratedKw) {
  if (!points || !(ratedKw > 0)) return null;
  const obs = points.map(([loadPct, effPct]) => {
    const pOut = (loadPct / 100) * ratedKw;
    const eff = effPct / 100;
    return [pOut, (pOut * (1 - eff)) / Math.max(eff, 1e-6)];
  });
  const n = obs.length;
  if (n < 2) return null;
  const sx = obs.reduce((a, [p]) => a + p, 0);
  const sy = obs.reduce((a, [, l]) => a + l, 0);
  const sxx = obs.reduce((a, [p]) => a + p * p, 0);
  const sxy = obs.reduce((a, [p, l]) => a + p * l, 0);
  const denom = n * sxx - sx * sx;
  if (Math.abs(denom) < 1e-12) return null;
  let k1 = (n * sxy - sx * sy) / denom;
  let k0 = (sy - k1 * sx) / n;
  k0 = Math.max(0, k0);
  k1 = Math.min(Math.max(0, k1), 0.2);
  // Datasheet convention, the same one the fit was solved against: the load
  // point is the power DELIVERED and the loss sits on top of it, so efficiency
  // is out/(out+loss). Reading it the other way round (loss subtracted from
  // the load point) understated it by up to a quarter of a point at 10% load
  // and biased the residual reported below.
  const effAt = (loadPct) => {
    const p = (loadPct / 100) * ratedKw;
    return p > 0 ? (p / (p + k0 + k1 * p)) * 100 : null;
  };
  const maxDev = points.reduce((worst, [loadPct, effPct]) => {
    const modelled = effAt(loadPct);
    return modelled == null ? worst : Math.max(worst, Math.abs(modelled - effPct));
  }, 0);
  return { k0, k1, effAt, maxDev, points };
}

/* The load points every converter datasheet reports, prefilled with the
   efficiencies the typical loss figures imply (0.5% of rated power standing,
   1.2% of throughput). Selecting the datasheet option therefore starts from
   exactly the behaviour the study would have used anyway — fitting this table
   returns the typical figures to within 0.01% — and the operator overwrites
   the efficiencies with the real ones. */
const PCS_TYPICAL_CURVE_POINTS = [
  [10, 94.2], [20, 96.4], [30, 97.2], [50, 97.8], [75, 98.2], [100, 98.3],
];
const PCS_TYPICAL_CURVE = PCS_TYPICAL_CURVE_POINTS.map(([l, e]) => `${l}:${e}`).join(", ");

function pcsRowsOrTemplate(raw) {
  const parsed = parsePcsCurveRows(raw);
  if (parsed.length) return parsed;
  return PCS_TYPICAL_CURVE_POINTS.map(([load, pct]) => ({
    load: String(load), pct: String(pct),
  }));
}

function PcsCurveEditor({ value, onChange }) {
  const [textMode, setTextMode] = useState(false);
  const [rows, setRows] = useState(() => pcsRowsOrTemplate(value));
  const lastEmitted = useRef(value || "");

  useEffect(() => {
    if ((value || "") !== lastEmitted.current) {
      setRows(pcsRowsOrTemplate(value));
      lastEmitted.current = value || "";
    }
  }, [value]);

  const emit = (nextRows) => {
    setRows(nextRows);
    const serialized = serializePcsCurveRows(nextRows);
    lastEmitted.current = serialized;
    onChange(serialized);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
          Efficiency vs Load — starts from typical values, replace with the datasheet
        </Typography>
        <Button
          size="small"
          onClick={() => setTextMode((prev) => !prev)}
          sx={{ textTransform: "none", fontSize: "0.72rem", py: 0, minWidth: 0, flexShrink: 0 }}
        >
          {textMode ? "Edit as table" : "Paste a table"}
        </Button>
      </Box>
      {textMode ? (
        <TextField
          fullWidth size="small" multiline minRows={3}
          value={value ?? ""}
          onChange={(e) => {
            lastEmitted.current = e.target.value;
            setRows(parsePcsCurveRows(e.target.value));
            onChange(e.target.value);
          }}
          placeholder={"10:94, 25:97.5, 50:98.2, 75:98.4, 100:98.3\nor two columns pasted from the datasheet"}
          helperText="One point per line or separated by commas: load % then efficiency %."
          sx={{ mt: 0.5 }}
        />
      ) : (
        <>
          <Stack spacing={0.75} sx={{ mt: 0.75 }}>
            {rows.length === 0 && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Enter the efficiency points from the converter datasheet — typically 10, 20,
                30, 50, 75 and 100% of rated power.
              </Typography>
            )}
            {rows.map((row, index) => (
              <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <TextField
                  size="small" type="number" label="Load %"
                  value={row.load}
                  onChange={(e) =>
                    emit(rows.map((r, k) => (k === index ? { ...r, load: e.target.value } : r)))
                  }
                  inputProps={{ min: 1, max: 100, step: 5 }}
                  sx={{ width: 100 }}
                />
                <TextField
                  size="small" type="number" label="Efficiency %"
                  value={row.pct}
                  onChange={(e) =>
                    emit(rows.map((r, k) => (k === index ? { ...r, pct: e.target.value } : r)))
                  }
                  inputProps={{ min: 50, max: 100, step: 0.1 }}
                  sx={{ width: 128 }}
                />
                <MaybeTooltip title="Remove this point" placement="top" arrow>
                  <span>
                    <IconButton size="small" onClick={() => emit(rows.filter((_, k) => k !== index))}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </span>
                </MaybeTooltip>
              </Box>
            ))}
          </Stack>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => emit([...rows, { load: "", pct: "" }])}
            sx={{ mt: 0.5, textTransform: "none" }}
          >
            Add point
          </Button>
        </>
      )}
    </Box>
  );
}

/* The two fields above are a formula the operator cannot see: a fixed term
   plus a share of throughput. Written out with the figures actually in force -
   including the typical values a blank field falls back to - it stops being two
   unrelated numbers. Same quantities and same layout as the datasheet-curve
   readout below, so the two routes into this model are read the same way. */
function PcsAffineReadout({ noLoadKw, marginalLoss, ratedKw }) {
  const rated = Number(ratedKw) || 0;
  const k0Raw = Number(noLoadKw);
  const k1Raw = Number(marginalLoss);
  const k0Blank = !Number.isFinite(k0Raw);
  const k1Blank = !Number.isFinite(k1Raw);
  // The defaults the engine substitutes for a blank field, so the readout shows
  // what the run will do rather than what the form happens to hold.
  const k0 = k0Blank ? 0.005 * rated : k0Raw;
  const k1 = k1Blank ? 0.012 : k1Raw;
  const effAt = (loadPct) => {
    const p = (rated * loadPct) / 100;
    return p > 0 ? (p / (p + k0 + k1 * p)) * 100 : null;
  };
  const full = effAt(100);
  const fmt = (v, d = 1) => (Number.isFinite(v) ? v.toFixed(d) : "—");
  return (
    <Box
      sx={{
        ...previewSurfaceSx, px: 1.5, py: 1,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block" }}>
        What the converter costs, hour by hour
      </Typography>
      <Typography
        sx={{
          fontSize: 14, mt: 0.5, color: "text.primary",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        }}
      >
        loss = {fmt(k0)} kW + {fmt(k1 * 100, 2)}% × power through the converter
      </Typography>
      {rated > 0 ? (
        <Box sx={{ display: "flex", gap: 2.5, mt: 0.75, flexWrap: "wrap" }}>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main", lineHeight: 1.15 }}>
              {fmt(k0 + k1 * rated)} kW
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>lost at full load</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main", lineHeight: 1.15 }}>
              {fmt(full)}%
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>at full load</Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main", lineHeight: 1.15 }}>
              {fmt(effAt(10))}%
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>at 10% load</Typography>
          </Box>
        </Box>
      ) : (
        <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 500, display: "block", mt: 0.5 }}>
          Select a battery to see what this costs — the figures are expressed at the
          converter&apos;s rated power.
        </Typography>
      )}
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
        {k0Blank && k1Blank
          ? "Both fields are blank, so the typical values above are in force."
          : k0Blank
          ? "The fixed loss is blank, so the typical 0.5% of rated power is in force."
          : k1Blank
          ? "The proportional loss is blank, so the typical 1.2% is in force."
          : "Both figures come from the datasheet entered above."}
        {" "}Efficiency falls away at part load because the fixed term is paid whatever
        the converter is moving.
      </Typography>
    </Box>
  );
}

/* Typical container-BESS auxiliaries, loaded when the toggle is switched on so
   that "on" means something. A toggle that is on while both cells are blank
   charges nothing at all, which is the state the panel used to open in.
   0.15% of rated power standing, plus 1.5 kW for every MW being moved, lands
   near 2% of discharged energy for a one-cycle-a-day container - the middle of
   the 1-3% band the report itself quotes. The readout underneath states the
   figure this project actually gets, so the assumption is checked rather than
   trusted. */
const AUX_TYPICAL_STANDING_FRACTION = 0.0015;
const AUX_TYPICAL_KW_PER_MW = 1.5;

/* Loaded when self-discharge is switched on: the middle of the 1-3%/month band
   quoted for lithium-ion, so the switch charges something the moment it is
   turned on rather than sitting on an empty cell that meant "off" anyway. */
const SELF_DISCHARGE_TYPICAL_PCT_MONTH = 2;

function typicalAuxLoads(ratedKw) {
  const rated = Number(ratedKw) || 0;
  return {
    auxIdleKw: rated > 0
      ? Number((rated * AUX_TYPICAL_STANDING_FRACTION).toFixed(2))
      : "",
    auxKwPerMwThroughput: AUX_TYPICAL_KW_PER_MW,
  };
}

/* What the two auxiliary figures cost over a year, and what share of the
   battery's own output that is - the number an operator can actually judge.
   Neither cell says anything on its own: one is a standing kW, the other a
   rate, and the interesting quantity is the sum against throughput. */
function AuxLoadReadout({ idleKw, kwPerMw, usableKwh, dailyCycles }) {
  const idle = Math.max(0, Number(idleKw) || 0);
  const perMw = Math.max(0, Number(kwPerMw) || 0);
  const usableMwh = (Number(usableKwh) || 0) / 1000;
  const cycles = Number(dailyCycles) || 0;
  const dischargedMwh = usableMwh * cycles * 365;
  const movedMwh = dischargedMwh * 2;             // charge plus discharge
  const standingMwh = (idle * 8760) / 1000;
  const workingMwh = (perMw * movedMwh) / 1000;
  const totalMwh = standingMwh + workingMwh;
  const sharePct = dischargedMwh > 0 ? (totalMwh / dischargedMwh) * 100 : null;
  const nothingSet = idle <= 0 && perMw <= 0;
  const fmt = (v, d = 1) => v.toLocaleString("en-GB", {
    minimumFractionDigits: d, maximumFractionDigits: d });
  return (
    <Box
      sx={{
        ...previewSurfaceSx, px: 1.5, py: 1,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block" }}>
        What the station consumes over a year
      </Typography>
      {nothingSet ? (
        <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 500, display: "block", mt: 0.5 }}>
          Both cells are empty, so nothing is charged — the same result as leaving this
          switched off. Enter a figure, or turn the switch off to say so plainly.
        </Typography>
      ) : (
        <>
          <Box sx={{ display: "flex", gap: 2.5, mt: 0.75, flexWrap: "wrap" }}>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main", lineHeight: 1.15 }}>
                {fmt(totalMwh)} MWh
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>per year</Typography>
            </Box>
            {sharePct != null && (
              <Box>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main", lineHeight: 1.15 }}>
                  {fmt(sharePct, 2)}%
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>of energy discharged</Typography>
              </Box>
            )}
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", lineHeight: 1.15 }}>
                {fmt(standingMwh)} / {fmt(workingMwh)}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>standing / while working (MWh)</Typography>
            </Box>
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
            {dischargedMwh > 0
              ? `At ${dailyCycles} cycle(s)/day. A containerised system typically lands between 1% and 3%.`
              : "Select a battery and set the daily cycles to see the share of throughput."}
          </Typography>
        </>
      )}
    </Box>
  );
}

/* The two outage inputs multiply into one number the engine actually applies -
   a power derate held across every hour. Showing it stops the pair being two
   percentages whose combined effect the reader has to work out. */
function AvailabilityReadout({ plannedHours, forcedPct, settlementAvailabilityPct }) {
  const batteryPerformance = {
    plannedOutageHoursYear: plannedHours,
    forcedOutageRatePct: forcedPct,
  };
  // The engine's expression, via the module the economics form shares, so this
  // panel cannot drift from the run or from the other page's warning.
  const factor = milpAvailabilityFactor(batteryPerformance);
  const lostHours = 8760 * (1 - factor);
  const doubleCount = availabilityModellingIssue({
    batteryPerformance,
    bessAvailabilityPct: settlementAvailabilityPct,
  });
  return (
    <Box
      sx={{
        ...previewSurfaceSx, px: 1.5, py: 1, height: "100%",
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block" }}>
        Applied to the dispatch as a power derate
      </Typography>
      <Box sx={{ display: "flex", gap: 2.5, mt: 0.75, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main", lineHeight: 1.15 }}>
            {(factor * 100).toFixed(1)}%
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>availability</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", lineHeight: 1.15 }}>
            {Math.round(lostHours).toLocaleString("en-GB")} h
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>equivalent out of service</Typography>
        </Box>
      </Box>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
        {factor >= 0.99999
          ? "Nothing entered, so the battery is treated as available every hour of the year."
          : "The two figures multiply; the derate holds at every hour rather than removing whole days."}
      </Typography>
      {doubleCount?.kind === "double_count" ? (
        <Typography
          variant="caption"
          sx={{ color: "warning.main", display: "block", mt: 0.5, fontWeight: 600 }}
        >
          {`BESS Availability is also set to ${(doubleCount.settlementFactor * 100).toFixed(0)}% `
            + "on the Economics page (Revenue Quality), which derates revenue a second time - "
            + `together they settle at ${(doubleCount.combinedFactor * 100).toFixed(2)}%. `
            + "Set that field back to 100 and let these two figures carry availability."}
        </Typography>
      ) : null}
    </Box>
  );
}

function PcsFitReadout({ curveRaw, ratedKw }) {
  const points = parsePcsCurvePoints(curveRaw);
  const fit = fitPcsFromCurve(points, ratedKw);
  if (!points) {
    return (
      <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 500, display: "block" }}>
        At least two points are needed. Until then the study uses the typical fixed and
        proportional losses (0.5% of rated power and 1.2% of throughput).
      </Typography>
    );
  }
  if (!fit) {
    return (
      <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 500, display: "block" }}>
        Select a battery first — the fit is expressed at the converter&apos;s rated power.
      </Typography>
    );
  }
  const poor = fit.maxDev > 1.0;
  return (
    <Box
      sx={{
        ...previewSurfaceSx, px: 1.5, py: 1,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block" }}>
        Fitted from the datasheet
      </Typography>
      <Box sx={{ display: "flex", gap: 2.5, mt: 0.75, flexWrap: "wrap" }}>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main", lineHeight: 1.15 }}>
            {fit.k0.toFixed(1)} kW
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>fixed loss</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main", lineHeight: 1.15 }}>
            {(fit.k1 * 100).toFixed(2)}%
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>of throughput</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main", lineHeight: 1.15 }}>
            {fit.effAt(100).toFixed(1)}%
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>at full load</Typography>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main", lineHeight: 1.15 }}>
            {fit.effAt(10).toFixed(1)}%
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>at 10% load</Typography>
        </Box>
      </Box>
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 0.75, color: poor ? "warning.dark" : "text.secondary", fontWeight: poor ? 500 : 400 }}
      >
        {poor
          ? `The straight-line model departs from the datasheet by up to ${fit.maxDev.toFixed(2)} percentage points — check the points below the knee of the curve.`
          : `Matches the ${points.length} datasheet points to within ${fit.maxDev.toFixed(2)} percentage points.`}
      </Typography>
    </Box>
  );
}

/* ---------- Battery Performance live preview ----------
   Client-side mirror of Configuration/battery_performance.py, so the user
   SEES the exact curves the run will use: the retention path per degradation
   mode (with the trigger-mode augmentation restart, or the fom_pct hold at
   nameplate) and the load-dependent one-way PCS efficiency (k0 + k1·P). */
/* Line colours and chart furniture, resolved per mode. Baked hexes here
   used to leave the dark theme with a blue curve on slate gridlines, so
   both now come from the theme: `main` is what the run actually uses,
   `base` the raw curve behind it. */
function usePreviewPalette() {
  const { palette } = useTheme();
  return React.useMemo(
    () => ({
      colors: getPreviewAccents(palette.mode),
      chrome: getChartChrome(palette.mode),
      tooltip: getRechartsTooltipProps(palette.mode),
    }),
    [palette.mode]
  );
}

/* Reference cell price for the suggested augmentation outlay. The lower bound
   of the turnkey band the report already benchmarks against, so the suggestion
   is conservative and traceable rather than invented. Both this rate and the
   capacity shortfall it multiplies are shown on the button. */
const AUGMENTATION_REF_EUR_PER_KWH = 140;

function formatEurCompact(value) {
  const n = Number(value) || 0;
  return n >= 1000 ? `€${Math.round(n / 1000).toLocaleString("en-US")}k` : `€${Math.round(n)}`;
}

function parseRetentionCurvePreview(raw) {
  const anchors = [];
  String(raw || "")
    .replace(/;/g, ",")
    .split(",")
    .forEach((tok) => {
      const parts = tok.split(":");
      if (parts.length !== 2) return;
      const year = Number(parts[0]);
      const pct = Number(parts[1]);
      if (Number.isFinite(year) && year >= 1 && Number.isFinite(pct) && pct > 0) {
        anchors.push([Math.round(year), pct / 100]);
      }
    });
  return anchors.sort((a, b) => a[0] - b[0]);
}

/* `funding` mirrors the engine rule: capacity is restored only in the year an
   augmentation outlay is booked, and an outlay that IS booked restores the
   capacity it pays for. Without it the preview would draw a step the run does
   not produce. */
function buildRetentionPreview(bpCfg, projectYears, funding = {}) {
  const mode = bpCfg.degradationMode ?? "annual_pct";
  if (mode === "legacy") return null;
  const T = Math.max(1, Math.round(projectYears) || 20);
  const anchors =
    mode === "warranty_curve" ? parseRetentionCurvePreview(bpCfg.retentionCurve) : [];
  const degPct = Number(bpCfg.bessAnnualDegPct);
  const deg = Math.max(0, Math.min(100, Number.isFinite(degPct) ? degPct : 2)) / 100;
  const base = [];
  if (anchors.length) {
    // Linear interpolation between anchors, last-slope extrapolation beyond —
    // same shape the engine's build_retention_factors produces.
    const pts = [[0, 1.0], ...anchors];
    for (let t = 1; t <= T; t += 1) {
      let value = pts[pts.length - 1][1];
      const last = pts[pts.length - 1];
      if (t >= last[0]) {
        if (pts.length >= 2) {
          const prev = pts[pts.length - 2];
          const slope = (last[1] - prev[1]) / Math.max(1, last[0] - prev[0]);
          value = last[1] + slope * (t - last[0]);
        }
      } else {
        for (let i = 1; i < pts.length; i += 1) {
          if (t <= pts[i][0]) {
            const [y0, v0] = pts[i - 1];
            const [y1, v1] = pts[i];
            value = v0 + ((v1 - v0) * (t - y0)) / Math.max(1, y1 - y0);
            break;
          }
        }
      }
      base.push(Math.max(0, value));
    }
  } else {
    for (let t = 1; t <= T; t += 1) base.push((1 - deg) ** (t - 1));
  }
  const augMode = bpCfg.augmentationMode ?? "manual";
  const augFunded = (Number(funding.cost) || 0) > 0;
  let effective = base;
  let augYear = null;        // restoration actually modelled
  let augYearUnfunded = null; // crossing that buys nothing (no outlay)
  const restartAt = (year) => base.map((v, k) => (k < year - 1 ? v : base[k - (year - 1)]));
  if (augMode === "fom_pct") {
    effective = base.map(() => 1.0);
  } else if (augMode === "trigger") {
    const trig =
      Math.max(0, Math.min(100, Number(bpCfg.augmentationTriggerPct ?? 80))) / 100;
    const idx = base.findIndex((v) => v < trig);
    if (idx >= 0) {
      if (augFunded) {
        augYear = idx + 1;
        effective = restartAt(augYear);
      } else {
        augYearUnfunded = idx + 1;
      }
    }
  } else {
    const manualYear = Math.round(Number(funding.manualYear) || 0);
    if (augFunded && manualYear >= 1 && manualYear <= T) {
      augYear = manualYear;
      effective = restartAt(augYear);
    }
  }
  const allValues = base.concat(effective);
  return {
    mode,
    augMode,
    augYear,
    augYearUnfunded,
    retentionAt: (year) =>
      base[Math.min(Math.max(1, Math.round(year)), T) - 1],
    yMin: Math.max(0, Math.floor(Math.min(...allValues) * 100) - 3),
    fallbackNote:
      mode === "pysam_derived"
        ? "Physics-based degradation is not available yet — the annual percentage applies."
        : mode === "warranty_curve" && !anchors.length
        ? "No warranty table entered yet — showing the annual percentage the run will use."
        : "",
    differs: effective.some((v, k) => Math.abs(v - base[k]) > 1e-9),
    triggerPct:
      augMode === "trigger" ? Number(bpCfg.augmentationTriggerPct ?? 80) : null,
    data: base.map((v, k) => ({
      year: k + 1,
      base: Number((v * 100).toFixed(2)),
      effective: Number((effective[k] * 100).toFixed(2)),
    })),
  };
}

function buildPcsPreview(bpCfg, ratedKw) {
  if ((bpCfg.pcsLossMode ?? "off") !== "affine" || !(ratedKw > 0)) return null;
  // A datasheet curve, when present, FITS the two loss terms and takes
  // precedence over the typed values — exactly as resolve_pcs_params does.
  const curvePoints = parsePcsCurvePoints(bpCfg.pcsEfficiencyCurve);
  const fitted = fitPcsFromCurve(curvePoints, ratedKw);
  const k0User = Number(bpCfg.pcsNoLoadKw);
  const k1User = Number(bpCfg.pcsMarginalLoss);
  const k0 = fitted
    ? fitted.k0
    : Number.isFinite(k0User) && k0User > 0
    ? k0User
    : 0.005 * ratedKw;
  const k1 = fitted
    ? fitted.k1
    : Number.isFinite(k1User) && k1User > 0
    ? Math.min(0.2, k1User)
    : 0.012;
  const gridTicks = [];
  for (let load = 5; load <= 100; load += 5) gridTicks.push(load);
  const datasheet = new Map((curvePoints || []).map(([l, e]) => [l, e]));
  const loads = [...new Set([...gridTicks, ...datasheet.keys()])].sort((a, b) => a - b);
  const data = loads.map((load) => {
    const p = (load / 100) * ratedKw;
    return {
      load,
      eff: Number(((p / (p + k0 + k1 * p)) * 100).toFixed(2)),
      datasheet: datasheet.has(load) ? Number(datasheet.get(load).toFixed(2)) : null,
    };
  });
  const lows = data.map((d) => d.eff).concat([...datasheet.values()]);
  return {
    k0,
    k1,
    data,
    gridTicks,
    fromCurve: Boolean(fitted),
    pointCount: curvePoints ? curvePoints.length : 0,
    yMin: Math.max(0, Math.floor(Math.min(...lows)) - 3),
  };
}

/* Environment mini-previews — the EXACT curves the engine applies.
   Temperature: the user's datasheet table when given, otherwise the standard
   typical-LFP band; linear interpolation, applied on the worst hour of each
   day. Self-discharge: per-hour decay pct/month / (30.4375 x 24), compounded
   while energy sits idle. */
const TEMP_EFF_POINTS = [
  [-10, 0.94],
  [0, 0.97],
  [10, 1.0],
  [35, 1.0],
  [45, 0.99],
  [50, 0.98],
];

/* Same tolerant parsing as the engine's config reader: "temp:eff %" pairs,
   percent normalised to a fraction, ≥2 distinct temperatures or null. */
function parseTempEffPoints(raw) {
  const map = new Map();
  tokenizeTempCurve(raw).forEach(([rawT, rawP]) => {
    const t = Number(rawT);
    const p = Number(rawP);
    if (Number.isFinite(t) && t >= -60 && t <= 90 && Number.isFinite(p) && p > 0) {
      map.set(t, Math.min(1, Math.max(0.1, p > 1.5 ? p / 100 : p)));
    }
  });
  const pts = [...map.entries()].sort((a, b) => a[0] - b[0]);
  return pts.length >= 2 ? pts : null;
}

function buildTempEffPreview(userCurveRaw) {
  const userPts = parseTempEffPoints(userCurveRaw);
  const pts = userPts || TEMP_EFF_POINTS;
  const interp = (t) => {
    if (t <= pts[0][0]) return pts[0][1];
    if (t >= pts[pts.length - 1][0]) return pts[pts.length - 1][1];
    for (let i = 1; i < pts.length; i += 1) {
      if (t <= pts[i][0]) {
        const [x0, y0] = pts[i - 1];
        const [x1, y1] = pts[i];
        return y0 + ((y1 - y0) * (t - x0)) / (x1 - x0);
      }
    }
    return 1;
  };
  const t0 = Math.min(-15, Math.floor(pts[0][0] / 5) * 5);
  const t1 = Math.max(50, Math.ceil(pts[pts.length - 1][0] / 5) * 5);
  const data = [];
  for (let t = t0; t <= t1; t += 5) {
    data.push({ t, factor: Number((interp(t) * 100).toFixed(1)) });
  }
  const minPct = Math.min(...pts.map(([, f]) => f)) * 100;
  return {
    data,
    custom: Boolean(userPts),
    yMin: Math.min(92, Math.max(0, Math.floor(minPct) - 2)),
  };
}

function buildSelfDischargePreview(pctMonth) {
  const pct = Number(pctMonth);
  if (!Number.isFinite(pct) || pct <= 0) return null;
  const lamHour = pct / 100 / (30.4375 * 24);
  const data = [];
  for (let day = 0; day <= 60; day += 5) {
    data.push({ day, soc: Number((Math.pow(1 - lamHour, 24 * day) * 100).toFixed(2)) });
  }
  const keep30 = Math.pow(1 - lamHour, 24 * 30.4375) * 100;
  return { data, keep30: Number(keep30.toFixed(1)) };
}

/* Numeric read-back of the interpolated curve beside the warranty table: it
   confirms what was typed produces the intended path, without repeating the
   chart that already lives in the preview further down. */
function RetentionCurveReadout({ bpCfg, projectYears }) {
  const { colors: previewColors } = usePreviewPalette();
  const preview = buildRetentionPreview(bpCfg, projectYears);
  const anchors = parseRetentionCurvePreview(bpCfg.retentionCurve);
  if (!preview || !anchors.length) return null;
  const T = Math.max(1, Math.round(projectYears) || 20);
  const at = (year) => {
    const row = preview.data.find((d) => d.year === year);
    return row ? row.base : null;
  };
  const marks = [...new Set([1, Math.max(2, Math.round(T / 2)), T])]
    .map((year) => ({ year, pct: at(year) }))
    .filter((m) => m.pct != null);
  // A sparkline of the shape being typed, beside the table that types it. The
  // full path lives in the preview at the foot of the section - seven panels
  // further down - because it can only be drawn once augmentation is known.
  // That is the right place for the complete answer and the wrong place for
  // the feedback you want while editing an anchor.
  const spark = (() => {
    const pts = preview.data
      .map((d) => [d.year, d.base])
      .filter(([, v]) => Number.isFinite(v));
    if (pts.length < 2) return null;
    // Room for labelled axes. The old box was 236x54 - too short for tick text,
    // which is why this used to lean on the caption to state the range instead.
    // A numbered vertical axis is what makes scaling to the data legitimate:
    // the reader sees a curve running 62-100 % and one running 99-100 % for
    // what they are, instead of two identical-looking slopes.
    const W = 320, H = 124, ML = 32, MR = 6, MT = 8, MB = 20;
    const plotW = W - ML - MR, plotH = H - MT - MB;

    let dataLo = Math.min(...pts.map(([, v]) => v));
    let dataHi = Math.max(...pts.map(([, v]) => v));
    // A flat curve has no range to tick; give it one rather than dividing by 0.
    if (dataHi - dataLo < 1) dataLo = Math.max(0, dataHi - 5);

    // "Nice" round tick values, so the axis reads 60 / 80 / 100 rather than
    // 61.6 / 80.8 / 100. Percentages, so never below 0 or above 100.
    const rawStep = (dataHi - dataLo) / 3;
    const mag = Math.pow(10, Math.floor(Math.log10(Math.max(rawStep, 1e-6))));
    const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((v) => v >= rawStep) || 10 * mag;
    const lo = Math.max(0, Math.floor(dataLo / step) * step);
    const hi = Math.min(100, Math.ceil(dataHi / step) * step);
    const span = Math.max(hi - lo, 1e-6);
    const yTicks = [];
    for (let v = lo; v <= hi + 1e-9; v += step) yTicks.push(Math.round(v * 100) / 100);

    const px = (y) => ML + ((y - 1) / Math.max(T - 1, 1)) * plotW;
    const py = (v) => MT + ((hi - v) / span) * plotH;
    const line = pts.map(([y, v]) => `${px(y).toFixed(1)},${py(v).toFixed(1)}`).join(" ");
    const area = `${px(1).toFixed(1)},${(MT + plotH).toFixed(1)} ${line} `
      + `${px(T).toFixed(1)},${(MT + plotH).toFixed(1)}`;

    // First, middle and last year - the same three the figures above name.
    const xTicks = [...new Set([1, Math.max(2, Math.round(T / 2)), T])].filter((y) => y <= T);

    return { W, H, ML, MT, plotW, plotH, line, area,
             yTicks: yTicks.map((v) => [v, py(v)]),
             xTicks: xTicks.map((y) => [y, px(y)]),
             anchorDots: anchors
      .filter(([y]) => y >= 1 && y <= T)
      .map(([y, f]) => [px(y), py(f * 100)]) };
  })();
  return (
    <Box
      sx={{
        ...previewSurfaceSx, px: 1.5, py: 1,
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block" }}>
        Capacity retained, as interpolated
      </Typography>
      <Box sx={{ display: "flex", gap: 2.5, mt: 0.75, flexWrap: "wrap" }}>
        {marks.map((m) => (
          <Box key={m.year}>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main", lineHeight: 1.15 }}>
              {m.pct}%
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              year {m.year}
            </Typography>
          </Box>
        ))}
      </Box>
      {spark && (
        <Box sx={{ mt: 1 }}>
          {/* Gridlines, tick labels and axis text take their colour from the
              theme via currentColor, so the chart follows light/dark instead of
              carrying baked hex values that turn near-white on a dark surface.

              Uniform scale only (no preserveAspectRatio="none"): stretching x
              independently of y turns the anchor dots into ellipses. */}
          <Box sx={{ color: "text.secondary" }}>
            <svg
              viewBox={`0 0 ${spark.W} ${spark.H}`}
              width="100%"
              style={{ maxWidth: spark.W, height: "auto", display: "block" }}
              role="img"
              aria-label="Retention curve, capacity retained by project year"
            >
              {spark.yTicks.map(([v, y]) => (
                <g key={`y${v}`}>
                  <line
                    x1={spark.ML} y1={y} x2={spark.ML + spark.plotW} y2={y}
                    stroke="currentColor" strokeWidth="1" opacity="0.22"
                    vectorEffect="non-scaling-stroke"
                  />
                  <text
                    x={spark.ML - 5} y={y + 3} textAnchor="end"
                    fontSize="9" fill="currentColor" opacity="0.75"
                  >
                    {v}%
                  </text>
                </g>
              ))}
              <polygon points={spark.area} fill={previewColors.main} opacity="0.10" />
              <polyline
                points={spark.line}
                fill="none"
                stroke={previewColors.main}
                strokeWidth="1.75"
                vectorEffect="non-scaling-stroke"
              />
              {spark.anchorDots.map(([cx, cy], i) => (
                <circle key={i} cx={cx} cy={cy} r="2.1" fill={previewColors.main} />
              ))}
              {spark.xTicks.map(([yr, x]) => (
                <text
                  key={`x${yr}`}
                  x={x} y={spark.MT + spark.plotH + 13} textAnchor="middle"
                  fontSize="9" fill="currentColor" opacity="0.75"
                >
                  {yr}
                </text>
              ))}
              <text
                x={spark.ML + spark.plotW} y={spark.H - 1} textAnchor="end"
                fontSize="8.5" fill="currentColor" opacity="0.6"
              >
                project year
              </text>
            </svg>
          </Box>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
            Dots mark the anchors you entered.
          </Typography>
        </Box>
      )}
      <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 0.75 }}>
        {anchors.length} anchor year{anchors.length === 1 ? "" : "s"} entered. Augmentation, where
        it applies, steps this path back up — the combined result is in the preview at the foot
        of this section.
      </Typography>
    </Box>
  );
}

function PerformancePreview({ bpCfg, ratedKw, projectYears, augFunding }) {
  const tr = useTranslateText();
  const {
    colors: previewColors,
    chrome: chartChrome,
    tooltip: rechartsTooltipProps,
  } = usePreviewPalette();
  const retention = buildRetentionPreview(bpCfg, projectYears, augFunding);
  const pcs = buildPcsPreview(bpCfg, ratedKw);
  const systemRte = Number(bpCfg.systemRtePct);
  const axisTick = { fontSize: 11, fill: chartChrome.tick };
  return (
    <SubSection
      title="Performance Preview"
      description="Live preview computed with the exact formulas of the simulation — what you set above is what the run will use."
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
            Battery retention over the project ({Math.max(1, Math.round(projectYears) || 20)} years)
          </Typography>
          {retention ? (
            <>
              <Box sx={{ height: 240, mt: 0.5 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={retention.data}
                    margin={{ top: 8, right: 12, bottom: 4, left: -18 }}
                  >
                    <CartesianGrid stroke={chartChrome.grid} strokeDasharray="2 4" vertical={false} />
                    <XAxis
                      dataKey="year"
                      tick={axisTick}
                      tickLine={false}
                      axisLine={{ stroke: chartChrome.axisLine }}
                    />
                    <YAxis
                      domain={[retention.yMin, 102]}
                      tick={axisTick}
                      tickLine={false}
                      axisLine={false}
                      unit="%"
                    />
                    <RechartsTooltip
                      {...rechartsTooltipProps}
                      formatter={(value) => `${value}%`}
                      labelFormatter={(label) => tr(`Project year ${label}`)}
                    />
                    {retention.differs && <Legend wrapperStyle={{ fontSize: 11 }} />}
                    {retention.triggerPct != null && (
                      <ReferenceLine
                        y={retention.triggerPct}
                        stroke={chartChrome.alert}
                        strokeDasharray="4 4"
                        label={{
                          value: tr(`trigger ${retention.triggerPct}%`),
                          fontSize: 10,
                          fill: chartChrome.alert,
                          position: "insideBottomLeft",
                        }}
                      />
                    )}
                    <Line
                      type="linear"
                      dataKey="base"
                      name={tr("Retention curve")}
                      stroke={retention.differs ? previewColors.base : previewColors.main}
                      strokeWidth={2}
                      strokeDasharray={retention.differs ? "5 4" : undefined}
                      dot={false}
                    />
                    {retention.differs && (
                      <Line
                        type="linear"
                        dataKey="effective"
                        name={tr(
                          retention.augMode === "fom_pct"
                            ? "Effective (FOM-maintained)"
                            : "Effective (with augmentation)"
                        )}
                        stroke={previewColors.main}
                        strokeWidth={2}
                        dot={false}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
              {(retention.fallbackNote || retention.augYear || retention.augYearUnfunded) && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    color: retention.augYearUnfunded ? "warning.dark" : "text.secondary",
                    fontWeight: retention.augYearUnfunded ? 500 : 400,
                  }}
                >
                  {retention.fallbackNote}
                  {retention.augYear
                    ? ` Capacity restored in year ${retention.augYear}, matching the augmentation outlay.`
                    : ""}
                  {retention.augYearUnfunded
                    ? ` The curve reaches the trigger in year ${retention.augYearUnfunded}, but no augmentation outlay is set — no restoration is modelled.`
                    : ""}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
              Uniform PV factor mode: one factor scales all revenue streams — there is
              no separate battery curve to preview.
            </Typography>
          )}
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>
            PCS one-way efficiency vs load
          </Typography>
          {pcs ? (
            <>
              <Box sx={{ height: 240, mt: 0.5 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={pcs.data} margin={{ top: 8, right: 12, bottom: 4, left: -18 }}>
                    <CartesianGrid stroke={chartChrome.grid} strokeDasharray="2 4" vertical={false} />
                    <XAxis
                      dataKey="load"
                      type="number"
                      domain={[0, 100]}
                      ticks={pcs.gridTicks}
                      tick={axisTick}
                      tickLine={false}
                      axisLine={{ stroke: chartChrome.axisLine }}
                      unit="%"
                    />
                    <YAxis
                      domain={[pcs.yMin, 100]}
                      tick={axisTick}
                      tickLine={false}
                      axisLine={false}
                      unit="%"
                    />
                    <RechartsTooltip
                      {...rechartsTooltipProps}
                      formatter={(value, name) => [`${value}%`, name]}
                      labelFormatter={(label) => tr(`Load ${label}%`)}
                    />
                    <Line
                      type="monotone"
                      dataKey="eff"
                      name={tr("Modelled efficiency")}
                      stroke={previewColors.main}
                      strokeWidth={2}
                      dot={false}
                    />
                    {pcs.fromCurve && (
                      <Scatter
                        dataKey="datasheet"
                        name={tr("Datasheet point")}
                        fill={chartChrome.datasheet}
                        shape="circle"
                      />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                {pcs.fromCurve
                  ? `Fitted from ${pcs.pointCount} datasheet points (shown as dots): fixed loss `
                  : "Fixed loss "}
                {pcs.k0.toFixed(1)} kW, proportional loss{" "}
                {(pcs.k1 * 100).toFixed(2)}% of throughput — at a rated{" "}
                {Math.round(ratedKw).toLocaleString("en-US")} kW (active battery discharge
                power × count).
              </Typography>
            </>
          ) : Number.isFinite(systemRte) && systemRte > 0 ? (
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
              A system round-trip efficiency of {systemRte}% is in use: it replaces the whole
              conversion chain, so there is nothing load-dependent to plot.
            </Typography>
          ) : (
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
              Set Converter (PCS) Losses to “Load-dependent” (and select a battery) to
              preview the efficiency across the operating range.
            </Typography>
          )}
        </Grid>
      </Grid>
    </SubSection>
  );
}

function BESSKnowledgePanel({ cfg, detailsData }) {
  // Selected battery characteristics (if available)
  const chars = detailsData?.characteristics || {};
  const effC = chars.Eff_charge;
  const effD = chars.Eff_discharge;

  // Safe numeric helpers
  const toNumber = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const toFrac = (v) => {
    const n = toNumber(v);
    if (n == null) return null;
    return n <= 1 ? n : n / 100;
  };

  const effCFrac = toFrac(effC);
  const effDFrac = toFrac(effD);

  const roundTripEff =
    effCFrac != null && effDFrac != null ? effCFrac * effDFrac : null;

  const fmtPct = (x) => (x == null ? "—" : `${(x * 100).toFixed(1)}%`);

  // ---- Degradation cost (€/kWh throughput) -> convert to €/MWh throughput below
  // NOTE: Your original formula had potential NaNs and also used cfg.batCount?.dummy.
  // Keep behavior but make it safe.
  const price = toNumber(chars.price);
  const energy = toNumber(chars.energy);
  const cycles = toNumber(chars.cycles);
  const socMax = toNumber(chars.soc_max);
  const socMin = toNumber(chars.soc_min);

  const batCount =
    toNumber(cfg?.batCount?.dummy) ??
    toNumber(cfg?.batCount) ??
    1;

  const usableFrac =
    socMax != null && socMin != null ? (socMax - socMin) / 100 : null;

  // €/kWh_throughput
  const degCostEurPerKWh =
    price != null && energy != null && cycles != null && usableFrac != null && usableFrac > 0
      ? (price * batCount) / (energy * usableFrac * cycles)
      : null;

  // €/MWh_throughput
  const degCostEurPerMWh = degCostEurPerKWh != null ? degCostEurPerKWh * 1000 : null;

  const lossFraction =
    roundTripEff == null ? null : 1 - roundTripEff;

  return (
    <Accordion
      sx={{
        mt: 3,
        backgroundColor: "transparent",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        "&:before": { display: "none" },
      }}
      defaultExpanded={false}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontSize: "0.85rem",
            color: "text.primary",
          }}
        >
          Knowledge & Assumptions
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Alert
          severity="info"
          sx={{
            mb: 2,
            backgroundColor: (t) =>
              t.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : undefined,
          }}
        >
          These notes explain how the battery system is modeled in the platform and
          what technical and economic assumptions are used during dispatch optimization
          and revenue calculations.
        </Alert>

        <Grid container spacing={2}>
          {/* Round-trip losses */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : t.palette.background.paper,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Round-trip losses (RTE)
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  color: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.80)"
                      : t.palette.text.primary,
                }}
              >
                When the battery charges and later discharges, part of the energy is lost.
                In most models: <b>RTE ≈ η<sub>charge</sub> × η<sub>discharge</sub></b>.
                Losses appear as heat and internal electrical losses.
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                Example: if η<sub>c</sub>=95% and η<sub>d</sub>=95%, then RTE≈90.25%
                and losses≈9.75%.
              </Typography>

              <Divider sx={{ my: 1.5 }} />

              <Typography
                variant="caption"
                sx={{
                  color: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.70)"
                      : t.palette.text.primary,
                }}
              >
                Based on selected battery:
              </Typography>

              <Typography variant="body2">
                η<sub>c</sub>: {effC ?? "—"}
                {effC != null ? (Number(effC) <= 1 ? "" : "%") : ""}
                {"  "}·{"  "}
                η<sub>d</sub>: {effD ?? "—"}
                {effD != null ? (Number(effD) <= 1 ? "" : "%") : ""}
              </Typography>

              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Estimated RTE: <b>{roundTripEff == null ? "—" : fmtPct(roundTripEff)}</b>
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.75)"
                      : t.palette.text.primary,
                }}
              >
                Loss fraction: <b>{lossFraction == null ? "—" : fmtPct(lossFraction)}</b>
              </Typography>
            </Paper>
          </Grid>

          {/* SoC window */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : t.palette.background.paper,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                SoC window & usable capacity
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  color: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.80)"
                      : t.palette.text.primary,
                }}
              >
                Batteries rarely operate between 0–100% SoC. A narrower SoC window
                improves lifetime but reduces usable energy.
              </Typography>

              <Typography variant="body2">
                Usable energy ≈ Nameplate energy × (SoC<sub>max</sub> − SoC<sub>min</sub>)
              </Typography>

              <Divider sx={{ my: 1.5 }} />

              <Typography
                variant="caption"
                sx={{
                  color: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.70)"
                      : t.palette.text.primary,
                }}
              >
                Why it matters:
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.80)"
                      : t.palette.text.primary,
                }}
              >
                A smaller SoC window lowers throughput and degradation, but it also
                limits arbitrage and ancillary service capacity.
              </Typography>
            </Paper>
          </Grid>

          {/* Round-trip loss cost + cycling economics */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : t.palette.background.paper,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Round-trip loss cost (€/MWh)
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  color: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.80)"
                      : t.palette.text.primary,
                }}
              >
                Due to inefficiencies, a fraction of energy is lost each time the battery cycles.
                Those losses translate directly into a monetary cost.
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Loss fraction</b> = 1 − (η<sub>c</sub> × η<sub>d</sub>)
              </Typography>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Efficiency example:</b>
                <br />
                η<sub>c</sub> = 95%, η<sub>d</sub> = 95%
                <br />
                η<sub>RT</sub> = 0.95 × 0.95 = <b>0.9025</b>
                <br />
                → Losses ≈ <b>9.75%</b>
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                To discharge <b>1 MWh</b>, the battery must charge:
                <br />
                <b>E<sub>in</sub> = 1 / η<sub>RT</sub> = 1.108 MWh</b>
                <br />
                → Lost energy ≈ <b>0.108 MWh</b>
              </Typography>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Cost example (€/MWh):</b>
                <br />
                Charging price = <b>70 €/MWh</b>
                <br />
                Loss cost = (1 / η<sub>RT</sub> − 1) × Price
                <br />
                = 0.108 × 70 ≈ <b>7.6 €/MWh</b>
              </Typography>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                🔷 Marginal cycling cost vs market spread
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mb: 1,
                  color: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.80)"
                      : t.palette.text.primary,
                }}
              >
                Profitability depends on the market price spread vs the marginal cost of cycling.
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Marginal cost (€/MWh)</b> =
                <br />• Round-trip loss cost
                <br />• + Degradation cost
              </Typography>

              <Divider sx={{ my: 1.5 }} />

              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Example:</b>
                <br />
                Round-trip loss cost = 7.6 €/MWh
                <br />
                Degradation cost ={" "}
                <b>{degCostEurPerMWh == null ? "—" : `${degCostEurPerMWh.toFixed(1)} €/MWh`}</b>
                <br />
                <b>
                  Total marginal cost ={" "}
                  {degCostEurPerMWh == null
                    ? "—"
                    : `${(degCostEurPerMWh + 7.6).toFixed(1)} €/MWh`}
                </b>
              </Typography>

              <Typography variant="body2" sx={{ mb: 1 }}>
                If the market spread is:
                <br />• 10 €/MWh → cycling <b>loses money</b>
                <br />• 20 €/MWh → cycling <b>creates value</b>
              </Typography>

              <Divider sx={{ my: 1.5 }} />

              <Typography
                variant="caption"
                sx={{
                  color: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.70)"
                      : t.palette.text.primary,
                }}
              >
                Practical implication
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.80)"
                      : t.palette.text.primary,
                }}
              >
                Arbitrage or reserve activation is only profitable if the buy–sell price spread
                exceeds the combined marginal cost (losses + degradation). Otherwise, cycling destroys value.
              </Typography>
            </Paper>
          </Grid>

          {/* Optimization limitations */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : t.palette.background.paper,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Optimization scope & limitations
              </Typography>

              <Box
                component="ul"
                sx={{
                  m: 0,
                  pl: 2.2,
                  color: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.80)"
                      : t.palette.text.primary,
                }}
              >
                <li>
                  Dispatch is optimized <b>economically</b> (revenues, constraints), not as an
                  electrochemical model of the cells.
                </li>
                <li>
                  <b>Thermal behavior</b> and temperature-dependent limits/degradation are not
                  explicitly modeled unless added as constraints.
                </li>
                <li>
                  Results are intended for <b>feasibility studies</b> and strategy assessment,
                  not as an EMS-ready real-time controller.
                </li>
                <li>
                  If <b>Min Idle Time</b> is set, it is a realism constraint to avoid rapid
                  charge/discharge toggling (only enforced if enabled in the optimizer).
                </li>
              </Box>
            </Paper>
          </Grid>

          {/* Tips */}
          <Grid size={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: (t) =>
                  t.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : t.palette.background.paper,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Modeling notes
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: (t) =>
                    t.palette.mode === "dark"
                      ? "rgba(255,255,255,0.80)"
                      : t.palette.text.primary,
                  whiteSpace: "pre-line",
                }}
              >
                • Efficiency may be applied per timestep (charge/discharge separately).
                {"\n"}• If “Simultaneous charge & discharge” is enabled, the optimizer may exploit theoretical flexibility (often unrealistic).
                {"\n"}• Grid charging is subject to market/regulatory constraints depending on the country.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
}


// --------------------------------------------------------------------------------

export default function BESSForm({ onSubmit }) {
  const tr = useTranslateText();
  const isGreek = useIsGreek();
  const {
    colors: previewColors,
    chrome: chartChrome,
    tooltip: rechartsTooltipProps,
  } = usePreviewPalette();
  const history = useHistory();
  const cfg = useMemo(() => {
    return history.present ?? DEFAULT_CONFIG;
  }, [history.present]);

  const selectedBatteries = useMemo(() => cfg.bess?.selected ?? [], [cfg.bess?.selected]);
  const activeName = cfg.bess?.activeName ?? "";
  const effectiveActiveName =
    activeName && selectedBatteries.some((x) => x.name === activeName)
      ? activeName
      : (selectedBatteries[0]?.name ?? "");

  const getBatLabel = useCallback(
    (b) => String(b?.battery_name ?? b?.name ?? "").trim(),
    []
  );
  const getBatId = useCallback(
    (b) => b?.id ?? b?.battery_id ?? b?.batteryId ?? null,
    []
  );
  const getBatKey = useCallback(
    (b) => {
      const id = getBatId(b);
      return id != null ? `id:${id}` : `name:${getBatLabel(b)}`;
    },
    [getBatId, getBatLabel]
  );

  const activeBattery =
    selectedBatteries.find((b) => b.name === effectiveActiveName) ?? null;
  const [activeOverrideErrors, setActiveOverrideErrors] = useState({});
  const [eolSohInput, setEolSohInput] = useState(
    String(DEFAULT_BATTERY_OVERRIDES.eol_soh_pct)
  );
  const [error, setError] = useState("");
  const activeOverrides = {
    ...DEFAULT_BATTERY_OVERRIDES,
    ...(activeBattery?.overrides ?? {}),
  };
  // per-battery pack count
  const activePackCount = Number(activeBattery?.count ?? 1);
  const commitCfg = useCallback((next) => {
    setError("");
    history.commit(next);
    onSubmit?.(next);
  }, [history, onSubmit]);

  const updateActiveBattery = useCallback((patchFn) => {
    const nextSelected = (selectedBatteries || []).map((b) => {
      if (b.name !== effectiveActiveName) return b;
      return patchFn(b);
    });

    let nextCfg = cfg;
    nextCfg = setPathInObject(nextCfg, "bess.selected", nextSelected);
    commitCfg(nextCfg);
  }, [cfg, commitCfg, effectiveActiveName, selectedBatteries]);

  const setActiveOverride = useCallback((key, value) => {
    updateActiveBattery((b) => ({
      ...b,
      overrides: {
        ...DEFAULT_BATTERY_OVERRIDES,
        ...(b.overrides || {}),
        [key]: value,
      },
    }));
  }, [updateActiveBattery]);

  const setActiveCount = useCallback((value) => {
    updateActiveBattery((b) => ({
      ...b,
      count: value,
    }));
  }, [updateActiveBattery]);



  const [showDegDetails, setShowDegDetails] = useState(false);
  const { accessToken } = useAuth();
  const { catalogWriteAllowed, catalogWriteDisabledMessage, isAdminUser } = useCurrentUserAccess();


  const [addOpen, setAddOpen] = useState(false);
  const [batteryVisibility, setBatteryVisibility] = useState("global");
  const [batteryOrganizations, setBatteryOrganizations] = useState([]);
  const [catalogOrganizations, setCatalogOrganizations] = useState([]);
  const [catalogOrganizationsLoading, setCatalogOrganizationsLoading] = useState(false);
  const [catalogOrganizationsError, setCatalogOrganizationsError] = useState("");
  useEffect(() => {
    if (!isAdminUser || !addOpen) return;
    let active = true;
    setCatalogOrganizationsLoading(true);
    getCatalogOrganizations(accessToken).then((data) => {
      if (active) {
        setCatalogOrganizations(data?.organizations || []);
        setCatalogOrganizationsError("");
      }
    }).catch(() => {
      if (active) setCatalogOrganizationsError(isGreek ? "Δεν φορτώθηκαν οι οργανισμοί." : "Organizations could not be loaded.");
    }).finally(() => { if (active) setCatalogOrganizationsLoading(false); });
    return () => { active = false; };
  }, [isAdminUser, addOpen, accessToken, isGreek]);
  const [addSaving, setAddSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });


  const BATTERY_FIELDS = [
    // General
    { key: "Energy", label: "Energy", unit: "kWh", kind: "number", required: true, min: 0 },
    { key: "Capacity", label: "Capacity", unit: "Ah", kind: "number", required: true, min: 0 },
    { key: "Power", label: "Rated Power", unit: "kW", kind: "number", required: true, min: 0 },
    { key: "Price", label: "Price", unit: "€", kind: "number", required: true, min: 0 },
    {
      key: "Technology",
      label: "Technology",
      kind: "select",
      required: true,
      options: BATTERY_TECHNOLOGY_OPTIONS,
    },
    { key: "Vendor", label: "Manufacturer", kind: "text", required: true },

    // Power
    { key: "P_max_c", label: "Max charge power", unit: "kW", kind: "number", required: false, min: 0 },
    { key: "P_max_d", label: "Max discharge power", unit: "kW", kind: "number", required: false, min: 0 },

    // Electrical
    { key: "Vnom", label: "Nominal voltage", unit: "V", kind: "number", required: true, min: 0 },
    { key: "Vmin", label: "Minimum voltage", unit: "V", kind: "number", required: true, min: 0 },
    { key: "Vmax", label: "Maximum voltage", unit: "V", kind: "number", required: true, min: 0 },
    { key: "Vcut", label: "Cutoff voltage", unit: "V", kind: "number", required: true, min: 0 },
    { key: "Charge current", label: "Max charge current", unit: "A", kind: "number", required: true, min: 0 },
    { key: "I_disch_max", label: "Max discharge current", unit: "A", kind: "number", required: true, min: 0 },

    // Efficiency
    { key: "Eff_charge", label: "Charge efficiency", kind: "number", required: false, min: 0, max: 1, helper: "0–1 (e.g. 0.96)" },
    { key: "Eff_discharge", label: "Discharge efficiency", kind: "number", required: false, min: 0, max: 1, helper: "0–1 (e.g. 0.95)" },

    // SoC limits
    { key: "soc_min", label: "Minimum SOC", unit: "%", kind: "number", required: true, min: 0, max: 100 },
    { key: "soc_max", label: "Maximum SOC", unit: "%", kind: "number", required: true, min: 0, max: 100 },

    // Lifetime
    { key: "N_cycles", label: "Cycle life", unit: "cycles", kind: "number", required: true, min: 1 },

    // Physical
    { key: "Width", label: "Width", unit: "m", kind: "number", required: true, min: 0 },
    { key: "Length", label: "Length", unit: "m", kind: "number", required: true, min: 0 },
    { key: "Height", label: "Height", unit: "m", kind: "number", required: true, min: 0 },
    { key: "Weight", label: "Weight", unit: "kg", kind: "number", required: true, min: 0 },

    // Optional extras
    { key: "c-rate_c", label: "C-rate (charge)", kind: "number", required: false, min: 0 },
    { key: "c-rate_d", label: "C-rate (discharge)", kind: "number", required: false, min: 0 },
  ];


  const [errors, setErrors] = useState({});

  const isEmpty = (v) => v === "" || v === null || v === undefined;

  // allow "" while typing, otherwise only digits with optional decimal point
  const NUM_RE = /^(\d+(\.\d*)?)?$/;

  function validateField(field, value, allValues) {
    const v = value;

    // Required
    if (field.required && isEmpty(v)) return "Required";

    // If empty and not required, ok
    if (!field.required && isEmpty(v)) return null;

    // Type checks
    if (field.kind === "number") {
      const num = Number(v);
      if (Number.isNaN(num)) return "Must be a number";

      if (field.min != null && num < field.min) return `Min ${field.min}`;
      if (field.max != null && num > field.max) return `Max ${field.max}`;
    }

    // Cross-field checks (SoC and Voltages)
    if (field.key === "soc_min" || field.key === "soc_max") {
      const smin = Number(allValues.soc_min);
      const smax = Number(allValues.soc_max);
      if (!Number.isNaN(smin) && !Number.isNaN(smax) && !isEmpty(allValues.soc_min) && !isEmpty(allValues.soc_max)) {
        if (smin >= smax) return "soc_min must be < soc_max";
      }
    }

    if (field.key === "Vmin" || field.key === "Vnom" || field.key === "Vmax") {
      const vmin = Number(allValues.Vmin);
      const vnom = Number(allValues.Vnom);
      const vmax = Number(allValues.Vmax);
      if (!Number.isNaN(vmin) && !Number.isNaN(vnom) && !Number.isNaN(vmax)
          && !isEmpty(allValues.Vmin) && !isEmpty(allValues.Vnom) && !isEmpty(allValues.Vmax)) {
        if (!(vmin <= vnom && vnom <= vmax)) return "Expected Vmin ≤ Vnom ≤ Vmax";
      }
    }

    return null;
  }

  function validateAll(values) {
    const next = {};

    if (!values.battery_name || values.battery_name.trim().length < 2) {
      next.battery_name = "Battery name is required";
    }

    for (const f of BATTERY_FIELDS) {
      const err = validateField(f, values[f.key], values);
      if (err) next[f.key] = err;
    }

    return next;
  }


  const [newBattery, setNewBattery] = useState(() => {
    const obj = { battery_name: "" };
    BATTERY_FIELDS.forEach((f) => {
      obj[f.key] = "";
    });
    return obj;
  });


  
  // ---- battery catalog state ----
  const [batteryFilters, setBatteryFilters] = useState({
    minEnergy: "",
    maxEnergy: "",
    minPmaxC: "",
    maxPmaxC: "",
    minPmaxD: "",
    maxPmaxD: "",
  });
  const hasAnyBatteryFilter = Object.values(batteryFilters).some(
    (v) => v !== "" && v != null
  );
  const clearBatteryFilters = () => {
    setBatteryFilters({
      minEnergy: "",
      maxEnergy: "",
      minPmaxC: "",
      maxPmaxC: "",
      minPmaxD: "",
      maxPmaxD: "",
    });
  };

  const [batteryOptions, setBatteryOptions] = useState([]);
  const [batteryLoading, setBatteryLoading] = useState(false);
  const [batteryError, setBatteryError] = useState("");
  const [enableBatteryFilters, setEnableBatteryFilters] = useState(false);
  const selectedOptions = useMemo(() => {
    const set = new Set((selectedBatteries || []).map(getBatKey));
    if (effectiveActiveName) set.add(`name:${String(effectiveActiveName).trim()}`);
    return (batteryOptions || []).filter((o) => set.has(getBatKey(o)));
  }, [batteryOptions, selectedBatteries, effectiveActiveName, getBatKey]);

  const getBatteryOption = useCallback(
    (battery) => {
      if (!battery) return null;
      const key = battery._key ?? getBatKey(battery);
      return (batteryOptions || []).find((option) => getBatKey(option) === key) ?? null;
    },
    [batteryOptions, getBatKey]
  );

  const getBatteryEolSohValue = useCallback(
    (battery) => {
      if (!battery) return DEFAULT_BATTERY_OVERRIDES.eol_soh_pct;
      const option = getBatteryOption(battery);
      const metadataValue = pickFirstFiniteValue([
        battery?.eol_soh_pct,
        battery?.eolSohPct,
        battery?.characteristics?.eol_soh_pct,
        battery?.characteristics?.eolSohPct,
        battery?.characteristics?.EOL_SOH_PCT,
        battery?.characteristics?.["EOL SOH (%)"],
        battery?.characteristics?.["End of Life SOH (%)"],
        option?.eol_soh_pct,
        option?.eolSohPct,
        option?.characteristics?.eol_soh_pct,
        option?.characteristics?.eolSohPct,
        option?.characteristics?.EOL_SOH_PCT,
        option?.characteristics?.["EOL SOH (%)"],
        option?.characteristics?.["End of Life SOH (%)"],
      ]);
      const rawValue =
        battery?.overrides?.eol_soh_pct != null
          ? battery.overrides.eol_soh_pct
          : metadataValue;
      const numericValue = Number(rawValue);

      if (!Number.isFinite(numericValue)) return DEFAULT_BATTERY_OVERRIDES.eol_soh_pct;
      return Math.min(100, Math.max(0, numericValue));
    },
    [getBatteryOption]
  );

  const validatePercentOverride = useCallback((value) => {
    const trimmed = String(value ?? "").trim();
    if (!trimmed) return "Enter a value from 0 to 100.";
    const numericValue = Number(trimmed);
    if (!Number.isFinite(numericValue)) return "Enter a numeric percentage from 0 to 100.";
    if (numericValue < 0 || numericValue > 100) return "Value must be between 0 and 100.";
    return "";
  }, []);

  useEffect(() => {
    if (!activeBattery) {
      setEolSohInput("");
      setActiveOverrideErrors((prev) => ({ ...prev, eol_soh_pct: "" }));
      return;
    }

    setEolSohInput(String(getBatteryEolSohValue(activeBattery)));
    setActiveOverrideErrors((prev) => ({ ...prev, eol_soh_pct: "" }));
  }, [activeBattery, getBatteryEolSohValue]);

  useEffect(() => {
    if (!activeBattery || activeBattery?.overrides?.eol_soh_pct != null) return;
    setActiveOverride("eol_soh_pct", getBatteryEolSohValue(activeBattery));
  }, [activeBattery, getBatteryEolSohValue, setActiveOverride]);

  // A project already SAVED in the warranty-curve mode with no table is not a
  // usable state: the panel offers a table to fill while the run quietly uses
  // the annual percentage instead. Loading the typical shape only when the mode
  // is switched never reaches those projects, because the switch already
  // happened - so coerce the state here too, once, with the same anchors.
  //
  // `retentionCurveSource` is what makes this safe to repeat: it is written the
  // moment the operator touches the table, so a table they deliberately empty
  // stays empty instead of refilling itself under their hands. Only a table
  // that was never filled in the first place carries no declaration at all.
  useEffect(() => {
    const bp = cfg?.batteryPerformance;
    if (!bp || bp.degradationMode !== "warranty_curve") return;
    if (String(bp.retentionCurveSource ?? "").trim() !== "") return;
    if (parseRetentionCurvePreview(bp.retentionCurve).length) return;
    commitCfg(
      setPathInObject(
        setPathInObject(cfg, "batteryPerformance.retentionCurve", TYPICAL_RETENTION_CURVE),
        "batteryPerformance.retentionCurveSource",
        "typical",
      ),
    );
  }, [cfg, commitCfg]);


  // ---- details dialog state ----
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsError, setDetailsError] = useState("");
  const [detailsCache, setDetailsCache] = useState({});
  // Derive specs from loaded details
  const specs = detailsData ? extractBatterySpecs(detailsData) : null;

  const energy = specs?.energy ?? null;
  const soc_min = specs?.soc_min ?? null;
  const soc_max = specs?.soc_max ?? null;
  const price = specs?.price ?? null;
  const cycles = specs?.cycles ?? null;

  // Usable energy per pack (kWh) based on SoC window, fallback = nameplate
  let usablePerPack = null;
  if (energy != null) {
    if (soc_min != null && soc_max != null) {
      usablePerPack = energy * ((soc_max - soc_min) / 100);
    } else {
      usablePerPack = energy; // fallback if SoC window not in DB
    }
  }

  // Total usable energy for the whole BESS (all packs)
  const packCount = activePackCount;
  const totalUsableEnergy =
    usablePerPack != null && packCount > 0
      ? usablePerPack * packCount
      : null;

  const asFrac = (x) => {
    if (x == null) return null;
    const n = Number(x);
    if (!Number.isFinite(n)) return null;
    return n > 1 ? n / 100 : n;
  };
  const effC_frac = asFrac(specs?.effC);
  const effD_frac = asFrac(specs?.effD);
  const rte_frac =
    effC_frac != null && effD_frac != null ? effC_frac * effD_frac : null;


  const energy_kWh = Number(specs?.energy);
  const pC_kW = Number(specs?.pMaxC);
  const pD_kW = Number(specs?.pMaxD);

  const cRateCharge =
    Number.isFinite(energy_kWh) && energy_kWh > 0 && Number.isFinite(pC_kW)
      ? pC_kW / energy_kWh
      : null;

  const cRateDischarge =
    Number.isFinite(energy_kWh) && energy_kWh > 0 && Number.isFinite(pD_kW)
      ? pD_kW / energy_kWh
      : null;


  // New: degradation cost per kWh
  let degradationCostPerKWh = null;
  let lifetimeThroughputKWh = null;
  let totalCost = null;

  if (
    effectiveActiveName &&
    totalUsableEnergy != null &&
    totalUsableEnergy > 0 &&
    cycles != null &&
    cycles > 0 &&
    price > 0
  ) {
    totalCost = price * packCount;
    lifetimeThroughputKWh = totalUsableEnergy * cycles; // kWh
    degradationCostPerKWh = totalCost / lifetimeThroughputKWh;
  }

  const handleField = useCallback((path, value) => {
    setError("");
    const next = setPathInObject(cfg, path, value);
    history.commit(next);       // ✅ THIS makes Save enabled
    onSubmit?.(next);           // optional
  }, [cfg, history, onSubmit]);

  const openBatteryDetails = (batteryOptionOrSelected) => {
    setDetailsTarget({
      id: getBatId(batteryOptionOrSelected),
      name: getBatLabel(batteryOptionOrSelected),
    });
    setDetailsOpen(true);
  };

  const isStandaloneBess = cfg.systemType === "Standalone BESS";
  // Greek RES + storage regulatory framework: Category 11A (arbitrage only)
  // forbids grid charging — see config/regulatoryFramework.js.
  const regulatoryFrameworkCfg = useMemo(() => normalizeRegulatoryFramework(cfg), [cfg]);
  const isRegulatory11A =
    cfg.settlementType === "Arbitrage" && regulatoryFrameworkCfg.mode === "11A";
  // Behind-the-Meter: the grid switches below follow from the value streams
  // and the grid injection regime on the General page, not free toggles. Each
  // is shown at its implied value, locked, with the reason under it — the same
  // answers the General page commits and the run payload sends (config/btm.js),
  // and the backend enforces independently (read_config).
  const isBtm = isBtmSettlement(cfg.settlementType);
  const btmCfg = useMemo(() => (isBtm ? normalizeBtm(cfg) : null), [cfg, isBtm]);
  const btmCharging = isBtm ? btmGridChargingState(btmCfg, cfg) : null;
  const btmInjection = isBtm ? btmGridInjectionState(btmCfg, cfg) : null;
  // The battery's own share of the connection point (display only: it is not
  // a stored setting, the dispatch enforces it — see btm.js).
  const btmBatteryInjection = isBtm ? btmBatteryInjectionState(btmCfg, cfg) : null;
  const btmSimultaneousGridPv = isBtm
    ? btmSimultaneousGridPvState(btmCfg, cfg, cfg.simultaneousGridPv)
    : null;
  // What each switch actually is for this run, locked or not.
  const effectiveGridCharging = isStandaloneBess
    ? true
    : isRegulatory11A
    ? false
    : isBtm
    ? btmCharging.on
    : Boolean(cfg.allowGridCharging);
  const effectiveGridInjection = isBtm ? btmInjection.on : Boolean(cfg.allowGridInjection);


  useEffect(() => {
    if (!detailsOpen || !detailsTarget) return;

    let cancelled = false;

    async function load() {
      setDetailsLoading(true);
      setDetailsError("");

      // ✅ 1) serve from cache
      const cached = detailsCache[detailsTarget.name];
      if (cached) {
        setDetailsData(cached);
        setDetailsLoading(false);
        return;
      }

      // ✅ 2) else fetch
      setDetailsData(null);
      try {
        const data = await getBatteryDetails(detailsTarget.name);
        if (!cancelled) {
          setDetailsData(data);
          // ✅ 3) store in cache
          setDetailsCache((c) => ({ ...c, [detailsTarget.name]: data }));
        }
      } catch (e) {
        if (!cancelled) setDetailsError(e.message || "Failed to load battery details.");
      } finally {
        if (!cancelled) setDetailsLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [detailsOpen, detailsTarget, detailsCache]);


  useEffect(() => {
    if (!effectiveActiveName) {
      setDetailsData(null);
      return;
    }

    let cancelled = false;

    async function loadDetails() {
      setDetailsLoading(true);
      setDetailsError("");
      try {
        const data = await getBatteryDetails(effectiveActiveName);
        if (!cancelled) {
          setDetailsData(data);
        }
      } catch (err) {
        if (!cancelled) {
          setDetailsError(err.message || "Failed to load battery details.");
          setDetailsData(null);
        }
      } finally {
        if (!cancelled) {
          setDetailsLoading(false);
        }
      }
    }

    loadDetails();

    return () => {
      cancelled = true;
    };
  }, [effectiveActiveName]);


  // ---- remove a battery from the catalogue ----
  // Same rule as the PV modules and inverters: an organisation's own battery
  // always, a shared library battery only for an administrator. The server
  // enforces it independently (catalog._delete_catalog_item).
  const [batteryCatalogReload, setBatteryCatalogReload] = useState(0);
  const [batteryDelete, setBatteryDelete] = useState(null); // { key, name } | null
  const [batteryDeleteBusy, setBatteryDeleteBusy] = useState(false);
  const [batteryDeleteError, setBatteryDeleteError] = useState("");
  const canDeleteBattery = (option) => Boolean(option?.owned || (option?.shared && isAdminUser));
  const confirmBatteryDelete = async () => {
    if (!batteryDelete) return;
    setBatteryDeleteBusy(true);
    setBatteryDeleteError("");
    try {
      await deleteBattery(batteryDelete.name, accessToken);
      // Drop it from this project's selection: a deleted name left in the
      // config would save a project that cannot run.
      const nextSelected = (selectedBatteries || []).filter(
        (b) => (b?._key ?? `name:${b?.name}`) !== batteryDelete.key && b?.name !== batteryDelete.name
      );
      if (nextSelected.length !== (selectedBatteries || []).length) {
        const nextActive = nextSelected.some((x) => x.name === effectiveActiveName)
          ? effectiveActiveName
          : (nextSelected[0]?.name ?? "");
        let nextCfg = cfg;
        nextCfg = setPathInObject(nextCfg, "bess.selected", nextSelected);
        nextCfg = setPathInObject(nextCfg, "bess.activeName", nextActive);
        commitCfg(nextCfg);
      }
      setBatteryDelete(null);
      setBatteryCatalogReload((n) => n + 1);
    } catch (err) {
      setBatteryDeleteError(String(err?.message || err) || "Could not delete the item.");
    } finally {
      setBatteryDeleteBusy(false);
    }
  };

  // ---- load batteries when filters change ----

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setBatteryLoading(true);
      setBatteryError("");

      try {

        const toNumOrUndef = (v) => {
          if (v === "" || v === null || v === undefined) return undefined;
          const n = Number(v);
          return Number.isFinite(n) ? n : undefined;
        };
        const params = enableBatteryFilters
          ? {
              min_energy: toNumOrUndef(batteryFilters.minEnergy),
              max_energy: toNumOrUndef(batteryFilters.maxEnergy),
              min_p_max_charge: toNumOrUndef(batteryFilters.minPmaxC),
              max_p_max_charge: toNumOrUndef(batteryFilters.maxPmaxC),
              min_p_max_discharge: toNumOrUndef(batteryFilters.minPmaxD),
              max_p_max_discharge: toNumOrUndef(batteryFilters.maxPmaxD),
            }
          : {};

        const data = await getBatteries(params);

        if (!cancelled) setBatteryOptions(data || []);
      } catch (err) {
        if (!cancelled) {
          setBatteryError(err.message || "Failed to load batteries.");
          setBatteryOptions([]);
        }
      } finally {
        if (!cancelled) setBatteryLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [enableBatteryFilters, batteryFilters, batteryCatalogReload]);


  useEffect(() => {
    if (cfg.systemType !== "Standalone BESS") return;

    // only commit if a correction is needed (avoid loops)
    if (cfg.allowGridCharging !== true) handleField("allowGridCharging", true);
    if (cfg.simultaneousGridPv !== false) handleField("simultaneousGridPv", false);
  }, [cfg.systemType, cfg.allowGridCharging, cfg.simultaneousGridPv, handleField]); // (see note below)


  return (
    <>
      <Paper
        elevation={1}
        sx={{
          p: 2, mb: 3, borderRadius: 2,
          // Readable, consistent sizes for the explanatory text of the whole
          // page: field hints and inline notes share one size, section
          // descriptions (set on SubSection) one step above.
          "& .MuiFormHelperText-root": { fontSize: "0.78rem", lineHeight: 1.5 },
          "& .MuiTypography-caption": { fontSize: "0.78rem", lineHeight: 1.5 },
        }}
      >
        <SetupPageHeader
          title="Battery Energy Storage System Setup"
          description="Configure the battery energy storage system, core technical parameters, and operating strategy for project analysis."
          sx={{ mb: 2 }}
          manual={{ settlementType: cfg.settlementType, section: "battery" }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}





        {/* 1. Battery technical details + catalog */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                mb: 1,
                textTransform: "uppercase",
                letterSpacing: 0.5,
                fontSize: "0.85rem",
                color: "text.primary",
              }}
            >
              Battery Details
            </Typography>
          </AccordionSummary>



          <AccordionDetails>
            <Grid container spacing={2}>



              {/* --- Battery selection / filters --- */}
              <Grid size={12}>
                <SubSection
                  title="Battery selection"
                  description="Choose one or more catalog models — the highlighted chip below is the ACTIVE battery the sizing run uses. Narrow the catalog first with the optional filters."
                  action={
                    catalogWriteAllowed ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={() => setAddOpen(true)}
                        sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
                      >
                        Add custom battery
                      </Button>
                    ) : null
                  }
                >
                  <Grid container spacing={2}>
                    {/* Catalog filters — an inset, optional aid that narrows
                        the model list below; visually separated so filtering
                        does not blend with the selection itself. */}
                    <Grid size={12}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 1.5,
                          border: "1px dashed",
                          borderColor: "divider",
                          bgcolor: "action.hover",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={enableBatteryFilters}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setEnableBatteryFilters(checked);

                                if (!checked) {
                                  clearBatteryFilters();
                                }
                              }}
                            />
                          }
                          label="Filter the catalog"
                        />

                        {enableBatteryFilters && (
                          <Button
                            size="small"
                            startIcon={<ClearIcon fontSize="small" />}
                            variant="text"
                            onClick={clearBatteryFilters}
                            disabled={!hasAnyBatteryFilter}
                            sx={{
                              textTransform: "none",
                              fontSize: "0.8rem",
                              color: "text.secondary",
                            }}
                          >
                            Clear filters
                          </Button>
                        )}
                        </Box>

                        {enableBatteryFilters && (
                          <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                        {/* Energy */}
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Min Energy (kWh)"
                            type="number"
                            value={batteryFilters.minEnergy}
                            onChange={(e) => setBatteryFilters((f) => ({ ...f, minEnergy: e.target.value }))}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Max Energy (kWh)"
                            type="number"
                            value={batteryFilters.maxEnergy}
                            onChange={(e) => setBatteryFilters((f) => ({ ...f, maxEnergy: e.target.value }))}
                          />
                        </Grid>

                        {/* Pmax charge */}
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Min Charge Power (kW)"
                            type="number"
                            value={batteryFilters.minPmaxC}
                            onChange={(e) => setBatteryFilters((f) => ({ ...f, minPmaxC: e.target.value }))}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <MaybeTooltip title="Minimum charge power in kW." placement="top" arrow>
                                    <IconButton size="small" tabIndex={-1}>
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </MaybeTooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Max Charge Power (kW)"
                            type="number"
                            value={batteryFilters.maxPmaxC}
                            onChange={(e) => setBatteryFilters((f) => ({ ...f, maxPmaxC: e.target.value }))}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <MaybeTooltip title="Maximum charge power in kW." placement="top" arrow>
                                    <IconButton size="small" tabIndex={-1}>
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </MaybeTooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        {/* Pmax discharge */}
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Min Discharge Power (kW)"
                            type="number"
                            value={batteryFilters.minPmaxD}
                            onChange={(e) => setBatteryFilters((f) => ({ ...f, minPmaxD: e.target.value }))}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <MaybeTooltip title="Minimum discharge power in kW." placement="top" arrow>
                                    <IconButton size="small" tabIndex={-1}>
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </MaybeTooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>

                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Max Discharge Power (kW)"
                            type="number"
                            value={batteryFilters.maxPmaxD}
                            onChange={(e) => setBatteryFilters((f) => ({ ...f, maxPmaxD: e.target.value }))}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <MaybeTooltip title="Maximum discharge power in kW." placement="top" arrow>
                                    <IconButton size="small" tabIndex={-1}>
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </MaybeTooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Grid>
                          </Grid>
                        )}
                      </Box>
                    </Grid>

                    {/* ✅ Battery models (MULTI SELECT with ticks) */}
                    <Grid size={12}>
                      <Autocomplete
                        multiple
                        disableCloseOnSelect
                        fullWidth
                        options={batteryOptions}
                        value={selectedOptions}
                        loading={batteryLoading}
                        disabled={!batteryLoading && batteryOptions.length === 0}
                        isOptionEqualToValue={(option, value) => getBatKey(option) === getBatKey(value)}
                        getOptionLabel={getBatLabel}
                        onChange={(_, newValues) => {
                          const nextSelected = (newValues || []).map((b) => {
                            const key = getBatKey(b);
                            const prev = (selectedBatteries || []).find((x) => x._key === key);

                            return (
                              prev ?? {
                                _key: key,
                                id: getBatId(b) ?? null,
                                name: getBatLabel(b),
                                energy: b?.energy ?? b?.characteristics?.Energy ?? null,
                                p_max_c: b?.p_max_c ?? b?.characteristics?.P_max_c ?? null,
                                p_max_d: b?.p_max_d ?? b?.characteristics?.P_max_d ?? null,
                                count: 1,
                                overrides: { ...DEFAULT_BATTERY_OVERRIDES },
                              }
                            );
                          });

                          const nextActive =
                            nextSelected.some((x) => x.name === effectiveActiveName)
                              ? effectiveActiveName
                              : (nextSelected[0]?.name ?? "");

                          let nextCfg = cfg;
                          nextCfg = setPathInObject(nextCfg, "bess.selected", nextSelected);
                          nextCfg = setPathInObject(nextCfg, "bess.activeName", nextActive);
                          commitCfg(nextCfg);
                        }}
                        renderTags={(tagValue, getTagProps) =>
                          tagValue.map((option, index) => {
                            const tagProps = getTagProps({ index });

                            return (
                              <Box
                                key={getBatKey(option)}
                                sx={{ display: "inline-flex", alignItems: "center", mr: 0.5, mb: 0.5 }}
                              >
                                <Chip
                                  {...tagProps}
                                  label={
                                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                      <span data-no-translate>{getBatLabel(option)}</span>

                                      <MaybeTooltip title="Battery info" placement="top" arrow>
                                        <IconButton
                                          size="small"
                                          onMouseDown={(e) => e.stopPropagation()} // prevent autocomplete focus weirdness
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            openBatteryDetails(option);
                                          }}
                                          sx={{ p: 0.25 }}
                                        >
                                          <InfoOutlinedIcon fontSize="inherit" />
                                        </IconButton>
                                      </MaybeTooltip>
                                      {canDeleteBattery(option) ? (
                                        <MaybeTooltip title="Remove from equipment catalogue" placement="top" arrow>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            aria-label="Remove from equipment catalogue"
                                            data-testid="battery-catalog-delete"
                                            onMouseDown={(e) => e.stopPropagation()}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setBatteryDeleteError("");
                                              setBatteryDelete({ key: getBatKey(option), name: getBatLabel(option) });
                                            }}
                                            sx={{ p: 0.25 }}
                                          >
                                            <DeleteOutlineIcon fontSize="inherit" />
                                          </IconButton>
                                        </MaybeTooltip>
                                      ) : null}
                                    </Box>
                                  }
                                />
                              </Box>
                            );
                          })
                        }
                        renderOption={(props, option, { selected }) => (
                          <li {...props}>
                            <Checkbox
                              checked={selected}
                              icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                              checkedIcon={<CheckBoxIcon fontSize="small" />}
                              sx={{
                                mr: 1,
                                "&.Mui-checked": { color: "primary.main" },
                              }}
                            />
                            <span data-no-translate>{getBatLabel(option)}</span>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            label="Battery models"
                            placeholder={batteryLoading ? "Loading..." : "Select one or more batteries"}
                            helperText={batteryError || "Select one or more batteries"}
                            error={Boolean(batteryError)}
                          />
                        )}
                      />
                      <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <Chip
                          size="small"
                          label={`${tr("Selected:")} ${selectedBatteries.length}`}
                          variant={selectedBatteries.length ? "filled" : "outlined"}
                        />
                        {effectiveActiveName && (
                          <Chip size="small" data-no-translate color={selectedBatteries.length > 1 ? "primary" : "default"}
                            label={selectedBatteries.length > 1
                              ? `${isGreek ? "Μπαταρία της μελέτης:" : "Study battery:"} ${effectiveActiveName}`
                              : `${tr("Active:")} ${effectiveActiveName}`} />
                        )}
                      </Box>
                      {selectedBatteries.length > 1 && effectiveActiveName ? (() => {
                        const others = selectedBatteries.map((b) => b.name).filter((n) => n && n !== effectiveActiveName);
                        const list = others.join(", ");
                        return (
                          <Alert severity="info" sx={{ mt: 1 }} data-no-translate data-testid="study-battery-info">
                            {isGreek
                              ? `Η μελέτη και οι αναφορές της αφορούν την μπαταρία ${effectiveActiveName}. ${others.length === 1 ? `Η ${list} συγκρίνεται` : `Οι υπόλοιπες (${list}) συγκρίνονται`} μαζί της στη «Σύγκριση μπαταριών», στη σελίδα Προσομοίωση.`
                              : `The study and its reports are about ${effectiveActiveName}. ${others.length === 1 ? `${list} is` : `The others (${list}) are`} compared with it in “Battery comparison” on the Simulation page.`}
                            {selectedBatteries.length > 4
                              ? (isGreek
                                ? " Η σύγκριση δέχεται έως 3 μπαταρίες εκτός από την μπαταρία της μελέτης· διαλέγετε ποιες στη σελίδα Προσομοίωση."
                                : " The comparison takes up to 3 batteries besides the study battery; you choose which on the Simulation page.")
                              : ""}
                          </Alert>
                        );
                      })() : null}
                    </Grid>
                    {/* ✅ Active battery switcher */}
                    {selectedBatteries.length > 0 && (
                      <Grid size={12}>
                        {selectedBatteries.length > 1 ? (
                          <Typography variant="caption" data-no-translate sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
                            {isGreek
                              ? "Πατήστε μια μπαταρία για να γίνει η μπαταρία της μελέτης. Τα πεδία παρακάτω αφορούν την μπαταρία της μελέτης."
                              : "Click a battery to make it the study battery. The fields below apply to the study battery."}
                          </Typography>
                        ) : null}
                        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                          {selectedBatteries.map((b) => (
                            <Chip
                              key={b.name}
                              label={b.name}
                              clickable
                              onClick={() => {
                                // ✅ update both
                                let nextCfg = cfg;
                                nextCfg = setPathInObject(nextCfg, "bess.activeName", b.name);
                                commitCfg(nextCfg);
                              }}
                              variant={b.name === effectiveActiveName ? "filled" : "outlined"}
                            />
                          ))}
                        </Stack>
                      </Grid>
                    )}

                    {/* This topology controls the optimization, not just the SLD. */}
                    <Grid size={12}>
                      <Divider sx={{ mt: 0.5 }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: "text.secondary",
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 0.5,
                          display: "block",
                          mt: 1,
                        }}
                      >
                        Battery / PV electrical coupling
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block", mt: 0.25 }}
                      >
                        Select how the battery connects to the PV system. This changes
                        the energy flows and dispatch results.
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        select fullWidth size="small" label="Battery coupling"
                        value={cfg?.bess?.topology || "ac-coupled"}
                        onChange={(event) => commitCfg(setPathInObject(cfg, "bess.topology", event.target.value))}
                        helperText="AC: battery has its own converter. DC: one shared PV inverter; PV can charge the battery before inverter clipping."
                      >
                        <MenuItem value="ac-coupled">AC-coupled (separate battery converter)</MenuItem>
                        <MenuItem value="dc-coupled">DC-coupled (shared hybrid inverter)</MenuItem>
                      </TextField>
                    </Grid>
                    {cfg?.bess?.topology === "dc-coupled" && (
                      <>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth size="small" type="number" required
                            label="Battery DC/DC efficiency (%)"
                            value={cfg?.bess?.dcDcEfficiencyPct ?? ""}
                            onChange={(event) => commitCfg(setPathInObject(cfg, "bess.dcDcEfficiencyPct", event.target.value))}
                            inputProps={{ min: 0, max: 100, step: 0.1 }}
                            error={cfg?.bess?.dcDcEfficiencyPct !== "" && !(Number(cfg?.bess?.dcDcEfficiencyPct) > 0 && Number(cfg?.bess?.dcDcEfficiencyPct) <= 100)}
                            helperText="Required datasheet value. No assumed default. Cell efficiency is configured separately."
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth size="small" type="number" required
                            label="Hybrid battery-port charge limit (kW DC)"
                            value={cfg?.bess?.dcPortChargeKw ?? ""}
                            onChange={(event) => commitCfg(setPathInObject(cfg, "bess.dcPortChargeKw", event.target.value))}
                            inputProps={{ min: 0, step: 0.1 }}
                            error={cfg?.bess?.dcPortChargeKw !== "" && !(Number(cfg?.bess?.dcPortChargeKw) > 0)}
                            helperText="Datasheet maximum at the shared DC bus, not the battery cell power."
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <TextField
                            fullWidth size="small" type="number" required
                            label="Hybrid battery-port discharge limit (kW DC)"
                            value={cfg?.bess?.dcPortDischargeKw ?? ""}
                            onChange={(event) => commitCfg(setPathInObject(cfg, "bess.dcPortDischargeKw", event.target.value))}
                            inputProps={{ min: 0, step: 0.1 }}
                            error={cfg?.bess?.dcPortDischargeKw !== "" && !(Number(cfg?.bess?.dcPortDischargeKw) > 0)}
                            helperText="Datasheet maximum at the shared DC bus; also limited by the inverter's common AC rating."
                          />
                        </Grid>
                        <Grid size={12}>
                          <Alert severity="info">
                            Supported now: behind-the-meter self-consumption/net billing, one PV inverter and one battery, charging from PV only. The selected inverter's AC rating is shared by PV and battery discharge; its nominal datasheet efficiency is used as a fixed dispatch approximation. The AC wiring losses after the inverter are applied to its whole AC output, PV and battery discharge alike. Grid charging, transformers, whole-system RTE overrides and legacy battery degradation are not supported in this DC mode. Representative later years are re-dispatched. Verify PV-string and battery-voltage compatibility against the equipment datasheets separately. The preliminary SLD can show the DC battery branch; its DC protection and cable sizing require final engineering verification.
                          </Alert>
                        </Grid>
                      </>
                    )}
                    <Grid size={12}>
                      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                        AC-coupled SLD annotations (do not affect simulation)
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="PCS model"
                        value={cfg?.bess?.pcs?.model || ""}
                        disabled={selectedBatteries.length === 0 || cfg?.bess?.topology !== "ac-coupled"}
                        onChange={(event) => {
                          const nextCfg = setPathInObject(cfg, "bess.pcs.model", event.target.value);
                          commitCfg(nextCfg);
                        }}
                        autoComplete="off"
                        inputProps={{ autoComplete: "off" }}
                        helperText="Free-text label printed on the PCS block of the SLD (e.g. a vendor model). Blank → the diagram shows 'TBD'."
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        label="PCS nominal power (kW)"
                        value={cfg?.bess?.pcs?.powerKw ?? ""}
                        disabled={selectedBatteries.length === 0 || cfg?.bess?.topology !== "ac-coupled"}
                        onChange={(event) => {
                          const raw = event.target.value;
                          const nextCfg = setPathInObject(
                            cfg,
                            "bess.pcs.powerKw",
                            raw === "" ? "" : Number(raw)
                          );
                          commitCfg(nextCfg);
                        }}
                        autoComplete="off"
                        inputProps={{ autoComplete: "off" }}
                        helperText="Annotates the PCS block in the SLD. Used only when explicitly provided; no rating is invented."
                      />
                    </Grid>
                  </Grid>
                </SubSection>
              </Grid>


              <Grid size={12}>
                <SubSection
                  title="Operating parameters"
                  description="SoC strategy, cycling limits and pack sizing for the active battery."
                >
                <Grid container spacing={2}>
                  {/* Initial SoC, Start-of-day SoC, Daily cycles */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Initial SoC (%)"
                      type="number"
                      value={activeOverrides.iniSoC}
                      onChange={(e) => {
                        const raw = Number(e.target.value);
                        if (Number.isNaN(raw)) return;
                        const clamped = Math.min(100, Math.max(0, raw));
                        setActiveOverride("iniSoC", clamped);
                      }}
                      disabled={!activeBattery}
                      helperText="SOC at the beginning of the trading period"
                      inputProps={{
                        min: 0,
                        max: 100,
                        step: 1,
                      }}
                      InputLabelProps={{
                        sx: {
                          color: "primary.main",
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                          "&.Mui-focused": {
                            color: "primary.main",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Start-of-day SoC (%)"
                      type="number"
                      value={activeOverrides.startDaySoC}
                      onChange={(e) => {
                        const raw = Number(e.target.value);
                        if (Number.isNaN(raw)) return;
                        const bounded = Math.min(100, Math.max(0, raw));
                        setActiveOverride("startDaySoC", bounded);
                      }}
                      disabled={!activeBattery}
                      helperText="Minimum SOC at the beginning of the day"
                      inputProps={{
                        min: 0,
                        max: 100,
                        step: 1,
                      }}
                      InputLabelProps={{
                        sx: {
                          color: "primary.main",
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                          "&.Mui-focused": {
                            color: "primary.main",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Daily Cycles"
                      type="number"
                      value={activeOverrides.dailyCycles}
                      onChange={(e) => setActiveOverride("dailyCycles", Number(e.target.value))}
                      disabled={!activeBattery}
                      helperText="Maximum cycles per day"
                      InputLabelProps={{
                        sx: {
                          color: "primary.main",           // normal state
                          fontWeight: 600,
                          letterSpacing: "0.02em",

                          "&.Mui-focused": {
                            color: "primary.main",         // when focused
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="End of Life SOH (%)"
                      type="number"
                      value={eolSohInput}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setEolSohInput(raw);
                        const error = validatePercentOverride(raw);
                        setActiveOverrideErrors((prev) => ({
                          ...prev,
                          eol_soh_pct: error,
                        }));
                        if (!error) {
                          setActiveOverride("eol_soh_pct", Number(raw));
                        }
                      }}
                      disabled={!activeBattery}
                      error={Boolean(activeOverrideErrors.eol_soh_pct)}
                      helperText={
                        activeOverrideErrors.eol_soh_pct ||
                        "Battery state of health threshold reached at the configured cycle life."
                      }
                      inputProps={{
                        min: 0,
                        max: 100,
                        step: 1,
                      }}
                      InputLabelProps={{
                        sx: {
                          color: "primary.main",
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                          "&.Mui-focused": {
                            color: "primary.main",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Battery count"
                      type="number"
                      value={activePackCount}
                      onChange={(e) => {
                        const n = Number(e.target.value);
                        if (!Number.isFinite(n)) return;
                        setActiveCount(Math.max(1, Math.floor(n)));
                      }}
                      disabled={!activeBattery}
                      InputLabelProps={{
                        sx: {
                          color: "primary.main",
                          fontWeight: 600,
                          letterSpacing: "0.02em",
                          "&.Mui-focused": {
                            color: "primary.main",
                          },
                        },
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Min Idle Time"
                      type="number"
                      value={activeOverrides.minIdleMins ?? 0}
                      onChange={(e) => setActiveOverride("minIdleMins", Number(e.target.value))}
                      disabled={!activeBattery}
                      helperText="Minimum minutes between switching charge ↔ discharge (0 = disabled)."
                      InputLabelProps={{
                        sx: {
                          color: "primary.main",           // normal state
                          fontWeight: 600,
                          letterSpacing: "0.02em",

                          "&.Mui-focused": {
                            color: "primary.main",         // when focused
                          },
                        },
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">min</InputAdornment>,
                      }}
                    />
                  </Grid>
                  {/* Charge taper (dev spec C1): SoC-dependent charge power.
                      Off by default so existing projects keep their results. */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <LabeledSwitchWithHint
                      label="Charge Taper (CV phase)"
                      checked={Boolean(cfg?.chargeTaper?.enabled)}
                      onChange={(e) => {
                        const nextCfg = setPathInObject(cfg, "chargeTaper.enabled", e.target.checked);
                        commitCfg(nextCfg);
                      }}
                      hint="Above the knee SoC the BMS cuts charging current (constant-voltage phase): available charge power falls linearly to zero at the SoC ceiling."
                    />
                  </Grid>
                  {Boolean(cfg?.chargeTaper?.enabled) && (
                    <Grid size={{ xs: 12, md: 3 }}>
                      <TextField
                        fullWidth
                        label="Taper Knee SoC (%)"
                        type="number"
                        value={cfg?.chargeTaper?.kneeSocPct ?? 85}
                        onChange={(e) => {
                          // Store what the user types (clamping happens on
                          // blur - clamping per keystroke makes "85" -> 10).
                          const raw = e.target.value;
                          const nextCfg = setPathInObject(
                            cfg,
                            "chargeTaper.kneeSocPct",
                            raw === "" ? "" : Number(raw)
                          );
                          commitCfg(nextCfg);
                        }}
                        onBlur={(e) => {
                          const raw = Number(e.target.value);
                          const clamped = Number.isFinite(raw)
                            ? Math.min(100, Math.max(10, raw))
                            : 85;
                          const nextCfg = setPathInObject(cfg, "chargeTaper.kneeSocPct", clamped);
                          commitCfg(nextCfg);
                        }}
                        inputProps={{ min: 10, max: 100, step: 1 }}
                        helperText="From the cell datasheet; 85% is typical for LFP. Must be below the max SoC."
                        InputLabelProps={{
                          sx: {
                            color: "primary.main",
                            fontWeight: 600,
                            letterSpacing: "0.02em",
                            "&.Mui-focused": { color: "primary.main" },
                          },
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
                </SubSection>
              </Grid>

              <Grid size={12}>
                <SubSection
                  title="Derived characteristics"
                  description="Computed from the selected model, pack count and price — read-only."
                >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Usable Energy"
                      type="number"
                      value={
                        effectiveActiveName && totalUsableEnergy != null
                          ? totalUsableEnergy.toFixed(0)
                          : ""
                      }
                      helperText={
                        effectiveActiveName
                          ? "Estimated usable energy of the installed BESS."
                          : "Select a battery model to compute usable energy."
                      }
                      InputLabelProps={{
                        sx: {
                          color: "primary.main",           // normal state
                          fontWeight: 600,
                          letterSpacing: "0.02em",

                          "&.Mui-focused": {
                            color: "primary.main",         // when focused
                          },
                        },
                      }}
                      InputProps={{ readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">kWh</InputAdornment>
                        ),
                      }}
                      disabled={!effectiveActiveName}
                    />
                  </Grid>
                  {/* NEW: Degradation cost per kWh (auto, read-only) */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {/* Main read-only field */}
                      <TextField
                        fullWidth
                        label="Degradation Cost"
                        type="number"
                        value={
                          effectiveActiveName && degradationCostPerKWh != null
                            ? degradationCostPerKWh.toFixed(4)
                            : ""
                        }
                        helperText={
                          effectiveActiveName
                            ? "Economic cost of battery wear per kWh cycled."
                            : "Select a battery to compute degradation cost."
                        }
                        InputLabelProps={{
                          sx: {
                            color: "primary.main",           // normal state
                            fontWeight: 600,
                            letterSpacing: "0.02em",

                            "&.Mui-focused": {
                              color: "primary.main",         // when focused
                            },
                          },
                          shrink: Boolean(effectiveActiveName && degradationCostPerKWh != null),
                        }}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">€/kWh</InputAdornment>
                          ),
                        }}
                        disabled={
                          !effectiveActiveName ||
                          !price ||
                          totalUsableEnergy == null ||
                          !cycles
                        }
                      />
                      {/* Toggle button */}
                      <Button
                        size="small"
                        onClick={() => setShowDegDetails((prev) => !prev)}
                        disabled={
                          !effectiveActiveName ||
                          degradationCostPerKWh == null ||
                          !price ||
                          totalUsableEnergy == null ||
                          !cycles
                        }
                        sx={{
                          alignSelf: "flex-start",
                          mt: 0.25,
                          textTransform: "none",
                          fontSize: "0.75rem",
                          p: 0,
                        }}
                      >
                        {showDegDetails ? "Hide calculation" : "Show calculation"}
                      </Button>
                      {/* Mini explanation panel */}
                      {showDegDetails && degradationCostPerKWh != null && (
                        <Box
                          sx={{
                            mt: 0.5,
                            p: 1,
                            borderRadius: 1,
                            bgcolor: (t) =>
                              t.palette.mode === "dark"
                                ? "rgba(255,255,255,0.04)"
                                : "rgba(0,0,0,0.02)",
                            border: "1px solid",
                            borderColor: "divider",
                            fontSize: "0.75rem",
                            color: "text.primary",
                          }}
                        >
                          <Typography variant="caption" component="div">
                            <strong>How this is calculated:</strong>
                          </Typography>

                          <Typography variant="caption" component="div">
                            Battery price per pack: {price?.toLocaleString("en-US")} € × {packCount} packs ={" "}
                            {totalCost?.toLocaleString("en-US")} € total.
                          </Typography>

                          <Typography variant="caption" component="div">
                            Usable energy: {usablePerPack?.toFixed(0)} kWh/pack × {packCount} ={" "}
                            {totalUsableEnergy?.toFixed(0)} kWh.
                          </Typography>

                          <Typography variant="caption" component="div">
                            Cycle life: {cycles?.toLocaleString("en-US")} full cycles.
                          </Typography>

                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            Lifetime throughput = {totalUsableEnergy?.toFixed(0)} kWh × {cycles} cycles ={" "}
                            {lifetimeThroughputKWh?.toLocaleString("en-US")} kWh.
                          </Typography>

                          <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                            Degradation cost = {totalCost?.toLocaleString("en-US")} € ÷{" "}
                            {lifetimeThroughputKWh?.toLocaleString("en-US")} kWh ={" "}
                            <strong>{degradationCostPerKWh.toFixed(4)} €/kWh</strong>.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Cell Charge Efficiency"
                      value={effC_frac == null ? "" : (effC_frac * 100).toFixed(2)}
                      helperText="One-way DC (cell) efficiency applied to charging. PCS and transformer losses are modelled separately (Battery Performance / Grid Connection)."
                      InputLabelProps={{
                        sx: {
                          color: "primary.main",           // normal state
                          fontWeight: 600,
                          letterSpacing: "0.02em",

                          "&.Mui-focused": {
                            color: "primary.main",         // when focused
                          },
                        },
                      }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      disabled={!effectiveActiveName}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Cell Discharge Efficiency"
                      value={effD_frac == null ? "" : (effD_frac * 100).toFixed(2)}
                      helperText="One-way DC (cell) efficiency applied to discharging. PCS and transformer losses are modelled separately."
                      InputLabelProps={{
                        sx: {
                          color: "primary.main",           // normal state
                          fontWeight: 600,
                          letterSpacing: "0.02em",

                          "&.Mui-focused": {
                            color: "primary.main",         // when focused
                          },
                        },
                      }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      disabled={!effectiveActiveName}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="Cell Round-trip Efficiency"
                      value={rte_frac == null ? "" : (rte_frac * 100).toFixed(2)}
                      helperText="Cell level: charge × discharge efficiency. Measured at the grid connection the round trip is lower, once converter and transformer losses are added — the report shows both figures side by side."
                      InputLabelProps={{
                        sx: {
                          color: "primary.main",           // normal state
                          fontWeight: 600,
                          letterSpacing: "0.02em",

                          "&.Mui-focused": {
                            color: "primary.main",         // when focused
                          },
                        },
                      }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                      }}
                      disabled={!effectiveActiveName}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="C-rate (charge)"
                      value={cRateCharge == null ? "" : cRateCharge.toFixed(2)}
                      helperText="Max charge power ÷ usable energy — 1.0 C means a full charge in about one hour."
                      InputLabelProps={{
                        sx: {
                          color: "primary.main",           // normal state
                          fontWeight: 600,
                          letterSpacing: "0.02em",

                          "&.Mui-focused": {
                            color: "primary.main",         // when focused
                          },
                        },
                      }}
                      InputProps={{ readOnly: true }}
                      disabled={!effectiveActiveName}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="C-rate (discharge)"
                      value={cRateDischarge == null ? "" : cRateDischarge.toFixed(2)}
                      helperText="Max discharge power ÷ usable energy — higher means a more power-oriented system."
                      InputLabelProps={{
                        sx: {
                          color: "primary.main",           // normal state
                          fontWeight: 600,
                          letterSpacing: "0.02em",

                          "&.Mui-focused": {
                            color: "primary.main",         // when focused
                          },
                        },
                      }}
                      InputProps={{ readOnly: true }}
                      disabled={!effectiveActiveName}
                    />
                  </Grid>
                </Grid>
                </SubSection>
              </Grid>

              <Grid size={12}>
                <SubSection
                  title="Site grid constraints"
                  description="Operating permissions at the grid interface — enforced as constraints in the optimization, across PV and BESS. The import/export power caps themselves are set on the Grid Connection page."
                >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Box display="flex" flexDirection="column" gap={1}>
                      {/* Allow Grid Charging */}
                      {/* Regulatory Category 11A (Greek RES + storage framework,
                          selected in General > Regulatory Framework) forbids
                          charging the battery from the grid: the toggle is
                          locked OFF. The backend enforces this independently. */}
                      {/* Behind-the-Meter drives this toggle from the selected
                          value streams instead: retail time-of-use arbitrage IS
                          grid charging, so it locks the toggle ON; without it the
                          battery charges from surplus generation only and the
                          toggle is locked OFF. Set on the General page. */}
                      <LabeledSwitchWithHint
                        label="Enable Grid Charging"
                        checked={effectiveGridCharging}
                        onChange={(e) => handleField("allowGridCharging", e.target.checked)}
                        disabled={isStandaloneBess || isRegulatory11A || isBtm}
                        hint={
                          isRegulatory11A
                            ? "Locked OFF by regulatory Category 11A: the battery may only charge from PV surplus / curtailed PV energy. Change the Regulatory Framework in the General page to re-enable."
                            : isBtm
                            ? btmCharging.hint
                            : isStandaloneBess
                            ? STANDALONE_GRID_CHARGING_HINT
                            : "Allows the battery to charge using grid electricity. Useful for arbitrage, but may be restricted by local regulations or settlement rules."
                        }
                      />
                      {isRegulatory11A && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                          Regulatory Category 11A: PV-only charging enforced.
                        </Typography>
                      )}
                      {isBtm && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                          {btmCharging.caption}
                        </Typography>
                      )}
                      {!isBtm && isStandaloneBess && !isRegulatory11A && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                          {STANDALONE_GRID_CHARGING_CAPTION}
                        </Typography>
                      )}
                      {/* Simultaneous PV + Grid Charging: a free modelling
                          choice whenever the battery may charge from the grid
                          and there is PV to combine it with. Behind the meter
                          that is retail ToU arbitrage on a hybrid site. */}
                      {(isBtm
                        ? btmSimultaneousGridPv.visible
                        : effectiveGridCharging && !isStandaloneBess) && (
                        <Box sx={{ ml: 3 }}>
                          <LabeledSwitchWithHint
                            label="Simultaneous PV + Grid Charging"
                            checked={Boolean(cfg.simultaneousGridPv)}
                            onChange={(e) =>
                              handleField("simultaneousGridPv", e.target.checked)
                            }
                            hint="Allows the battery to charge from PV and the grid at the same time. Some grid codes do not permit concurrent import and export flows."
                          />
                        </Box>
                      )}
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <LabeledSwitchWithHint
                      label="Simultaneous charging & discharging"
                      checked={Boolean(cfg.simultaneousChDis)}
                      onChange={(e) => handleField("simultaneousChDis", e.target.checked)}
                      hint="If enabled, the optimization may charge and discharge the battery in the same timestep. Physically this is inefficient, and most real systems prohibit it. Enable only if your modelling requires theoretical flexibility."
                    />
                  </Grid>

                  {/* Export permission at the connection point — PV surplus and
                      battery discharge together. Behind the meter the battery's
                      own line sits under it, so "PV exports, the battery does
                      not" reads as two switches instead of one ambiguous one. */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Box display="flex" flexDirection="column" gap={1}>
                      <LabeledSwitchWithHint
                        label={GRID_INJECTION_SWITCH_LABEL}
                        checked={effectiveGridInjection}
                        onChange={(e) => handleField("allowGridInjection", e.target.checked)}
                        disabled={isBtm}
                        hint={
                          isBtm
                            ? btmInjection.hint
                            : "If enabled, PV surplus or battery discharge may be exported to the grid, subject to export limit and settlement rules."
                        }
                      />
                      {isBtm && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                          {btmInjection.caption}
                        </Typography>
                      )}
                      {isBtm && !isStandaloneBess && (
                        <Box sx={{ ml: 3 }}>
                          <LabeledSwitchWithHint
                            label={BATTERY_INJECTION_SWITCH_LABEL}
                            checked={btmBatteryInjection.on}
                            onChange={() => {}}
                            disabled
                            hint={btmBatteryInjection.hint}
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", ml: 3 }}
                          >
                            {btmBatteryInjection.caption}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Simultaneous injecting & drawing: only meaningful while
                      export is allowed. Behind the meter there is one meter,
                      so it is locked OFF and says why. */}
                  {effectiveGridInjection && (
                    <Grid size={{ xs: 12, md: 3 }}>
                      <LabeledSwitchWithHint
                        label="Simultaneous injecting & drawing"
                        checked={isBtm ? false : Boolean(cfg.simultaneousInjectDraw)}
                        onChange={(e) => handleField("simultaneousInjectDraw", e.target.checked)}
                        disabled={isBtm}
                        hint={
                          isBtm
                            ? BTM_INJECT_DRAW_HINT
                            : "If enabled, the model allows import and export in the same timestep (only when regulations/metering allow it)."
                        }
                      />
                      {isBtm && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
                          {BTM_INJECT_DRAW_CAPTION}
                        </Typography>
                      )}
                    </Grid>
                  )}

                </Grid>
                </SubSection>

                {/* Daily dispatch windows — battery operating constraint
                    (arbitrage runs only). Stored under
                    arbitrageMerchant.regulatoryFramework where the backend
                    optimizer reads chargeWindow / dischargeWindow. */}
                {cfg.settlementType === "Arbitrage" && (
                  <Box sx={{ mt: 2 }}>
                    <DispatchWindowsSection
                      value={regulatoryFrameworkCfg}
                      onChange={(nextRf) =>
                        handleField("arbitrageMerchant.regulatoryFramework", nextRf)
                      }
                    />
                  </Box>
                )}

              </Grid>

            </Grid>
          </AccordionDetails>



        </Accordion>
        {/* Transformer settings live on the Grid Connection page (/Grid). */}

        {/* ==================== BATTERY PERFORMANCE (dev spec section 10) ====================
            Degradation & stream split (B1/B2), PCS losses (A1/A2), auxiliary
            loads (A5), availability (Γ3) and annual warranty limits (Γ2). */}
        <Accordion defaultExpanded sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.85rem", color: "text.primary" }}
            >
              Battery Performance
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {(() => {
              const bpCfg = cfg.batteryPerformance || {};
              const setBp = (key, value) =>
                commitCfg(setPathInObject(cfg, `batteryPerformance.${key}`, value));
              const numField = (key, raw) => setBp(key, raw === "" ? "" : Number(raw));
              // A supplied System RTE replaces the WHOLE conversion chain -
              // the backend forces PCS and the temperature leg off. Reflect
              // that in the UI instead of leaving the controls looking live.
              const rteOverrideActive = Boolean(bpCfg.systemRtePct);
              // ---- context from the rest of the project, for the hints ----
              const projectYearsNow = (() => {
                const parsed = [
                  cfg?.feasibility?.settlementPeriod,
                  cfg?.feasibility?.analysisYears,
                  cfg?.analysisYears,
                  cfg?.projectTermYears,
                ]
                  .map(Number)
                  .find((x) => Number.isFinite(x) && x > 0);
                return parsed ? Math.max(1, Math.round(parsed)) : 20;
              })();
              // The augmentation outlay lives in the economics block; it is
              // EDITED here, next to the mode that decides whether there is
              // one at all, and mirrored read-only under Investment Costs.
              const augFin = cfg?.feasibility?.economics?.financialParams || {};
              const setFinancial = (key, value) =>
                commitCfg(setPathInObject(
                  cfg, `feasibility.economics.financialParams.${key}`, value));
              const dailyCyclesNow = Number(activeOverrides?.dailyCycles) || 1;
              const afrrReservedMw =
                (Number(cfg?.balancingV1?.reservedUpMw) || 0) +
                (Number(cfg?.balancingV1?.reservedDownMw) || 0);
              const eolSohPct = Number(activeOverrides?.eol_soh_pct) || 80;
              const cyclesCap = Number(bpCfg.maxAnnualCycles);
              const capExhaustDay =
                Number.isFinite(cyclesCap) && cyclesCap > 0 && dailyCyclesNow > 0
                  ? Math.round(cyclesCap / dailyCyclesNow)
                  : null;
              const triggerBelowEol =
                (bpCfg.augmentationMode ?? "manual") === "trigger" &&
                Number(bpCfg.augmentationTriggerPct ?? 80) <= eolSohPct;
              return (
                <Stack spacing={2}>
                  <Typography sx={{ fontSize: "0.845rem", lineHeight: 1.55, color: "text.secondary" }}>
                    Model fidelity for the battery: degradation, conversion losses,
                    auxiliaries, availability, warranty limits and augmentation. New
                    projects start with standard assumptions; saved projects keep their
                    own values. Everything set here is disclosed in the report&apos;s
                    assumptions section.
                  </Typography>
                  <SubSection
                    title="Degradation"
                    description="Each revenue stream follows its own decay: battery revenue tracks the retention curve set here, PV revenue tracks the panel degradation factor."
                  >
                  {(() => {
                  const degMode = bpCfg.degradationMode ?? "annual_pct";
                  const isWarrantyCurve = degMode === "warranty_curve";
                  const warrantyAnchors = parseRetentionCurvePreview(bpCfg.retentionCurve);
                  // Still the loaded typical shape, or the operator's own table?
                  // Decided by whether the editor has emitted since, never by
                  // comparing strings - a project could legitimately carry a
                  // warranty table that happens to match.
                  const retentionIsTypical =
                    String(bpCfg.retentionCurveSource ?? "").trim().toLowerCase() === "typical";
                  // Selecting a mode that needs a table must LOAD that table.
                  // Leaving it empty and warning that the run falls back to a
                  // different mode is not a usable state to hand someone.
                  const setDegMode = (next) => {
                    let nextCfg = setPathInObject(
                      cfg, "batteryPerformance.degradationMode", next);
                    if (next === "warranty_curve" && !warrantyAnchors.length) {
                      nextCfg = setPathInObject(
                        nextCfg, "batteryPerformance.retentionCurve", TYPICAL_RETENTION_CURVE);
                      nextCfg = setPathInObject(
                        nextCfg, "batteryPerformance.retentionCurveSource", "typical");
                    }
                    commitCfg(nextCfg);   // one commit, so undo steps stay meaningful
                  };
                  // Any edit in the table makes the numbers the operator's, so
                  // the report stops calling them a manufacturer warranty.
                  const setRetentionCurve = (next) => {
                    let nextCfg = setPathInObject(
                      cfg, "batteryPerformance.retentionCurve", next);
                    nextCfg = setPathInObject(
                      nextCfg, "batteryPerformance.retentionCurveSource", "user");
                    commitCfg(nextCfg);
                  };
                  return (
                  <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      select fullWidth size="small" label="Degradation Mode"
                      value={degMode}
                      onChange={(e) => setDegMode(e.target.value)}
                      helperText={
                        degMode === "legacy"
                          ? "One factor scales every revenue stream — kept so this project's results do not change."
                          : degMode === "pysam_derived"
                          ? "Not available yet — the annual percentage below applies until it is."
                          : isWarrantyCurve
                          ? (retentionIsTypical
                              ? "Battery revenue follows the retention table beside this — still the typical shape, not a manufacturer's."
                              : "Battery revenue follows the manufacturer's warranty table.")
                          : "Battery revenue follows its own decay, separate from the panels."
                      }
                    >
                      <MenuItem value="annual_pct">Annual % (geometric)</MenuItem>
                      <MenuItem value="warranty_curve">OEM warranty curve</MenuItem>
                      {/* Offered only where the open project already uses it. */}
                      {degMode === "pysam_derived" && (
                        <MenuItem value="pysam_derived">Physics-based (not available yet)</MenuItem>
                      )}
                      {degMode === "legacy" && (
                        <MenuItem value="legacy">Uniform PV factor (this project&apos;s setting)</MenuItem>
                      )}
                    </TextField>
                  </Grid>

                  {!isWarrantyCurve && degMode !== "legacy" && (
                    <Grid size={{ xs: 12, md: 3 }}>
                      <TextField
                        fullWidth size="small" type="number" label="Battery Degradation (%/yr)"
                        value={bpCfg.bessAnnualDegPct ?? 2.0}
                        onChange={(e) => numField("bessAnnualDegPct", e.target.value)}
                        helperText="Capacity lost each year, compounding. Typical LFP: 2.0"
                        inputProps={{ min: 0, max: 10, step: 0.1 }}
                      />
                    </Grid>
                  )}

                  {isWarrantyCurve && (
                    <>
                      <Grid size={{ xs: 12, md: 5 }}>
                        <RetentionCurveEditor
                          value={bpCfg.retentionCurve ?? ""}
                          onChange={setRetentionCurve}
                        />
                        {!warrantyAnchors.length ? (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mt: 0.5, color: "warning.dark", fontWeight: 500 }}
                          >
                            The table is empty — the study falls back
                            to {Number(bpCfg.bessAnnualDegPct ?? 2.0)}%/yr. Add at least one
                            anchor year, or switch back to the annual percentage.
                          </Typography>
                        ) : retentionIsTypical ? (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                          >
                            Loaded with a typical LFP retention shape, equivalent to
                            {" "}{Number(bpCfg.bessAnnualDegPct ?? 2.0)}%/yr — replace it with the
                            manufacturer&apos;s table. Until you do, the report states these
                            figures as a typical assumption rather than warranty terms.
                          </Typography>
                        ) : null}
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <RetentionCurveReadout bpCfg={bpCfg} projectYears={projectYearsNow} />
                      </Grid>
                    </>
                  )}

                  {degMode === "legacy" && (
                    <Grid size={{ xs: 12, md: 9 }}>
                      <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                        The panel degradation factor scales battery revenue as well, which
                        understates how fast a battery loses capacity. Switch to the annual
                        percentage or the manufacturer&apos;s warranty table for a new assessment.
                      </Typography>
                    </Grid>
                  )}

                  </Grid>
                  );
                  })()}
                  </SubSection>

                  <SubSection
                    title="Conversion Losses"
                    description="Energy lost in the equipment between the cells and the grid connection. Either each stage is modelled from its own data, or one measured efficiency figure stands for the whole system. The two are alternatives — only the settings belonging to the selected approach are shown."
                  >
                  <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      select fullWidth size="small" label="Approach"
                      disabled={cfg?.bess?.topology === "dc-coupled"}
                      value={rteOverrideActive ? "measured" : "staged"}
                      onChange={(e) =>
                        setBp("systemRtePct", e.target.value === "measured" ? 85 : "")
                      }
                      helperText={
                        rteOverrideActive
                          ? "One figure covers cells, converter and transformers."
                          : "Each stage carries its own losses — the more accurate route."
                      }
                    >
                      <MenuItem value="staged">Model each stage</MenuItem>
                      <MenuItem value="measured">Single measured system efficiency</MenuItem>
                    </TextField>
                  </Grid>

                  {cfg?.bess?.topology === "dc-coupled" ? (
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Alert severity="info">
                        DC-coupled dispatch uses the battery cell efficiencies, the DC/DC efficiency above, and the selected shared inverter's nominal efficiency. A separate AC battery PCS and a whole-system RTE override are not applied. Clear any existing system RTE override before running.
                      </Alert>
                    </Grid>
                  ) : rteOverrideActive ? (
                    <>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth size="small" type="number" required
                          label="System Round-Trip Efficiency (%)"
                          value={bpCfg.systemRtePct ?? ""}
                          onChange={(e) => numField("systemRtePct", e.target.value)}
                          error={
                            bpCfg.systemRtePct !== "" &&
                            !(Number(bpCfg.systemRtePct) >= 10 && Number(bpCfg.systemRtePct) <= 100)
                          }
                          helperText="Measured from grid terminal to grid terminal. Typically 84–88% for a new AC-connected system."
                          inputProps={{ min: 10, max: 100, step: 0.5 }}
                        />
                      </Grid>
                      {/* Its own row: the two selectors above fill twelve columns
                          between them, and this note reads badly squeezed into
                          what is left of one. */}
                      <Grid size={{ xs: 12, md: 8 }}>
                        <Box
                          sx={{
                            border: "1px solid", borderColor: "warning.light", borderRadius: 1,
                            bgcolor: "warning.50", px: 1.5, py: 1,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: "warning.dark", fontWeight: 600, display: "block" }}>
                            This figure stands in for the whole chain
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
                            Converter losses, the transformer losses configured under Grid Connection
                            and the temperature effect on efficiency are all set aside for the run, so
                            nothing is counted twice. Use it when a commissioning or guarantee test
                            gives a single number for the system.
                          </Typography>
                        </Box>
                      </Grid>
                    </>
                  ) : (
                    (() => {
                    // The choice is stored, not inferred from whether the table
                    // happens to hold points yet: otherwise the datasheet option
                    // could never be picked, because picking it is precisely what
                    // has to come before entering the points. Projects saved
                    // before the choice existed are read from the table itself.
                    const pcsSourceStored = String(bpCfg.pcsLossSource ?? "").trim().toLowerCase();
                    const pcsHasCurve = String(bpCfg.pcsEfficiencyCurve ?? "").trim() !== "";
                    const pcsFromCurve =
                      pcsSourceStored === "curve" || (pcsSourceStored === "" && pcsHasCurve);
                    const pcsUiMode =
                      (bpCfg.pcsLossMode ?? "off") !== "affine"
                        ? "off"
                        : pcsFromCurve
                        ? "curve"
                        : "affine";
                    const setPcsUiMode = (next) => {
                      // One commit per switch, so undo steps stay meaningful.
                      let nextCfg = setPathInObject(
                        cfg, "batteryPerformance.pcsLossMode", next === "off" ? "off" : "affine");
                      nextCfg = setPathInObject(
                        nextCfg, "batteryPerformance.pcsLossSource",
                        next === "curve" ? "curve" : "typical");
                      if (next !== "curve") {
                        nextCfg = setPathInObject(nextCfg, "batteryPerformance.pcsEfficiencyCurve", "");
                      } else if (!pcsHasCurve) {
                        // Start from the typical curve rather than an empty
                        // table: the study behaves exactly as it did before the
                        // switch until the datasheet figures replace it.
                        nextCfg = setPathInObject(
                          nextCfg, "batteryPerformance.pcsEfficiencyCurve", PCS_TYPICAL_CURVE);
                      }
                      commitCfg(nextCfg);
                    };
                    const pcsRatedKw =
                      (Number(activeBattery?.p_max_d) || 0) * (Number(activePackCount) || 1);
                    return (
                    <>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          select fullWidth size="small" label="Converter (PCS) Losses"
                          value={pcsUiMode}
                          onChange={(e) => setPcsUiMode(e.target.value)}
                          helperText={
                            pcsUiMode === "off"
                              ? "Only the cells' own efficiency applies — for a catalog figure that already includes the converter."
                              : pcsUiMode === "curve"
                              ? "Enter the datasheet points; the two loss terms are derived from them."
                              : "A standby draw plus a share of whatever passes through, applied at every hour of the dispatch."
                          }
                        >
                          {/* Short enough that the menu never grows past the field
                              it drops out of: the popup takes its width from the
                              longest option and only its minimum from the control,
                              so a sentence here renders a panel wider than the
                              select. The distinction each option makes is carried
                              by the helper text below, where there is room for it. */}
                          <MenuItem value="off">Not modelled</MenuItem>
                          <MenuItem value="affine">Fixed + proportional</MenuItem>
                          <MenuItem value="curve">From efficiency curve</MenuItem>
                        </TextField>
                      </Grid>

                      {pcsUiMode === "curve" && (
                        <>
                          <Grid size={{ xs: 12, md: 5 }}>
                            <PcsCurveEditor
                              value={bpCfg.pcsEfficiencyCurve ?? ""}
                              onChange={(next) => setBp("pcsEfficiencyCurve", next)}
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 4 }}>
                            <PcsFitReadout
                              curveRaw={bpCfg.pcsEfficiencyCurve ?? ""}
                              ratedKw={pcsRatedKw}
                            />
                          </Grid>
                        </>
                      )}

                      {pcsUiMode === "affine" && (
                        <>
                          {/* Half-width, on the row below the two selectors. At a
                              quarter each the outlined labels were truncated to
                              "Fixed Loss when Energis..." and the helper text
                              stacked into a narrow column. */}
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth size="small" type="number"
                              label="Fixed Loss when Energised (kW)"
                              value={bpCfg.pcsNoLoadKw ?? ""}
                              onChange={(e) => numField("pcsNoLoadKw", e.target.value)}
                              placeholder="0.5% of rated power"
                              helperText="Power the converter consumes just by being switched on, whatever it is moving. Blank uses 0.5% of its rated power — typical when the datasheet does not state a figure."
                            />
                          </Grid>
                          <Grid size={{ xs: 12, md: 6 }}>
                            <TextField
                              fullWidth size="small" type="number"
                              label="Proportional Loss (fraction)"
                              value={bpCfg.pcsMarginalLoss ?? ""}
                              onChange={(e) => numField("pcsMarginalLoss", e.target.value)}
                              placeholder="0.012  (= 1.2%)"
                              helperText="Share of the power passing through that is lost in conversion — 0.012 means 1.2%. Blank uses the typical value, giving about 98% one-way efficiency at full load."
                              inputProps={{ min: 0, max: 0.2, step: 0.001 }}
                            />
                          </Grid>
                          {/* Full width, below both fields: it is a statement
                              about the two of them together, not about either
                              one. */}
                          <Grid size={{ xs: 12 }}>
                            <PcsAffineReadout
                              noLoadKw={bpCfg.pcsNoLoadKw}
                              marginalLoss={bpCfg.pcsMarginalLoss}
                              ratedKw={pcsRatedKw}
                            />
                          </Grid>
                        </>
                      )}
                    </>
                    );
                    })()
                  )}

                  </Grid>
                  </SubSection>

                  <SubSection
                    title="Auxiliary Loads"
                    description="The station's own consumption — thermal management, controls, protection — as explicit kW rather than an efficiency factor, so it is paid for even in hours the battery does not move energy."
                  >
                  {(() => {
                    // Stored, not inferred from the two figures: an operator who
                    // means to model auxiliaries should see the fields while
                    // filling them in, and one who does not should not have to
                    // leave cells blank to say so.
                    const auxStored = bpCfg.auxLoadsEnabled;
                    const auxOn =
                      auxStored === undefined || auxStored === null || auxStored === ""
                        ? Number(bpCfg.auxIdleKw) > 0 || Number(bpCfg.auxKwPerMwThroughput) > 0
                        : Boolean(auxStored);
                    const auxRatedKw =
                      (Number(activeBattery?.p_max_d) || 0) * (Number(activePackCount) || 1);
                    const auxBothBlank =
                      !(Number(bpCfg.auxIdleKw) > 0) && !(Number(bpCfg.auxKwPerMwThroughput) > 0);
                    const setAuxOn = (on) => {
                      let nextCfg = setPathInObject(cfg, "batteryPerformance.auxLoadsEnabled", on);
                      if (!on) {
                        nextCfg = setPathInObject(nextCfg, "batteryPerformance.auxIdleKw", "");
                        nextCfg = setPathInObject(nextCfg, "batteryPerformance.auxKwPerMwThroughput", "");
                      } else if (auxBothBlank) {
                        // Switching it on has to switch something on. Empty cells
                        // behind an enabled toggle charge nothing, which is the
                        // off state wearing the on state's clothes.
                        const typical = typicalAuxLoads(auxRatedKw);
                        nextCfg = setPathInObject(
                          nextCfg, "batteryPerformance.auxIdleKw", typical.auxIdleKw);
                        nextCfg = setPathInObject(
                          nextCfg, "batteryPerformance.auxKwPerMwThroughput",
                          typical.auxKwPerMwThroughput);
                      }
                      commitCfg(nextCfg);
                    };
                    return (
                      <>
                        <LabeledSwitchWithHint
                          label="Model auxiliary consumption"
                          checked={auxOn}
                          onChange={(e) => setAuxOn(e.target.checked)}
                          hint="Turn on for a containerised system whose datasheet states the HVAC and control-power draw. Either figure may be left blank or zero — that part of the consumption is then simply not modelled."
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", display: "block", ml: 0.5, mt: -0.5, mb: auxOn ? 1.5 : 0 }}
                        >
                          {auxOn
                            ? "Started from typical container figures — replace either with the datasheet value. Clearing a cell drops that part of the consumption."
                            : "Off: the study charges no station consumption. Results are then slightly optimistic for a containerised system with active cooling."}
                        </Typography>
                        {auxOn && (
                          <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <TextField
                                fullWidth size="small" type="number" label="Standing Load (kW)"
                                value={bpCfg.auxIdleKw ?? ""}
                                onChange={(e) => numField("auxIdleKw", e.target.value)}
                                helperText="Drawn every hour of the year, from the container datasheet — thermal management and controls. Leave blank if the datasheet does not state it."
                                inputProps={{ min: 0, step: 1 }}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <TextField
                                fullWidth size="small" type="number" label="Load per Throughput (kW per MW)"
                                value={bpCfg.auxKwPerMwThroughput ?? ""}
                                onChange={(e) => numField("auxKwPerMwThroughput", e.target.value)}
                                helperText="Additional draw while the battery is working, per MW of charging plus discharging. Leave blank if it is not stated separately."
                                inputProps={{ min: 0, step: 0.5 }}
                              />
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                              <AuxLoadReadout
                                idleKw={bpCfg.auxIdleKw}
                                kwPerMw={bpCfg.auxKwPerMwThroughput}
                                usableKwh={totalUsableEnergy}
                                dailyCycles={dailyCyclesNow}
                              />
                            </Grid>
                          </Grid>
                        )}
                      </>
                    );
                  })()}
                  </SubSection>

                  <SubSection
                    title="Availability & Warranty Limits"
                    description="Availability applies as a power derate across the year. The annual cycle and throughput caps are enforced in dispatch when set, so the study cannot buy revenue the warranty would not allow."
                  >
                  <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth size="small" type="number" label="Planned Outage (h/yr)"
                      value={bpCfg.plannedOutageHoursYear ?? ""}
                      onChange={(e) => numField("plannedOutageHoursYear", e.target.value)}
                      helperText="O&M practice: ~72"
                      inputProps={{ min: 0, max: 8760, step: 1 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth size="small" type="number" label="Forced Outage Rate (%)"
                      value={bpCfg.forcedOutageRatePct ?? ""}
                      onChange={(e) => numField("forcedOutageRatePct", e.target.value)}
                      helperText="Market practice: ~2.0"
                      inputProps={{ min: 0, max: 100, step: 0.1 }}
                    />
                  </Grid>
                  {/* The two outages and what they multiply into, on one row;
                      the warranty caps on the next. At a quarter each the
                      throughput label was truncated to "Max Annual Throughput (…"
                      and the cycle helper had no room for its warning. */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <AvailabilityReadout
                      plannedHours={bpCfg.plannedOutageHoursYear}
                      forcedPct={bpCfg.forcedOutageRatePct}
                      settlementAvailabilityPct={
                        cfg?.feasibility?.economics?.financialParams?.bessAvailabilityPct
                      }
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth size="small" type="number" label="Max Annual Cycles"
                      value={bpCfg.maxAnnualCycles ?? ""}
                      onChange={(e) => numField("maxAnnualCycles", e.target.value)}
                      helperText={
                        capExhaustDay && capExhaustDay < 365
                          ? `With ${dailyCyclesNow} cycle(s)/day (Battery Details) the annual budget is exhausted around day ${capExhaustDay}; dispatch throttles afterwards.`
                          : "Warranty cap, held inside the dispatch. Blank means no cap."
                      }
                      sx={
                        capExhaustDay && capExhaustDay < 365
                          ? { "& .MuiFormHelperText-root": { color: "warning.main" } }
                          : undefined
                      }
                      inputProps={{ min: 0, step: 1 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth size="small" type="number" label="Max Annual Throughput (MWh)"
                      value={bpCfg.maxAnnualThroughputMWh ?? ""}
                      onChange={(e) => numField("maxAnnualThroughputMWh", e.target.value)}
                      helperText="Warranty cap, held inside the dispatch. Blank means no cap."
                      inputProps={{ min: 0, step: 10 }}
                    />
                  </Grid>

                  </Grid>
                  </SubSection>

                  <SubSection
                    title="Environment & Standby Losses"
                    description="Physical effects beyond the cell: ambient temperature acting on the efficiency, and self-discharge of stored energy. They matter for outdoor cabinets and for strategies that hold charge for long periods."
                  >
                  <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Box>
                      {rteOverrideActive ? (
                        <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                          <b>Temperature effect on efficiency</b> — covered by the system
                          round-trip efficiency set under Conversion Losses. Switch to modelling
                          each stage to make it temperature-dependent.
                        </Typography>
                      ) : (
                      <>
                      <LabeledSwitchWithHint
                        label="Temp → Efficiency"
                        checked={Boolean(bpCfg.tempEfficiencyEnabled)}
                        onChange={(e) => setBp("tempEfficiencyEnabled", e.target.checked)}
                        hint="Each day, the worst hour's ambient temperature scales the round-trip efficiency through the curve below — half of the factor on each leg, on top of the existing power derate. For PASSIVE / outdoor cabinets; an HVAC-conditioned container holds the cells near 20–25 °C, so keep this off and model the HVAC in Auxiliary Loads instead."
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block", ml: 0.5, mt: -0.5 }}
                      >
                        {Boolean(bpCfg.tempEfficiencyEnabled)
                          ? `The curve below is applied on each day's worst hour.${isStandaloneBess ? " Standalone BESS: the ambient temperature comes from the project's weather source — make sure one is configured." : ""}`
                          : "Off: temperature derates only the available POWER; efficiency stays flat year-round. Enable for outdoor cabinets in cold climates."}
                      </Typography>
                      </>
                      )}
                      {Boolean(bpCfg.tempEfficiencyEnabled) && !rteOverrideActive && (() => {
                        const tempPrev = buildTempEffPreview(bpCfg.tempEfficiencyCurve);
                        return (
                          <>
                            <TempCurveEditor
                              value={bpCfg.tempEfficiencyCurve ?? ""}
                              onChange={(v) => setBp("tempEfficiencyCurve", v)}
                            />
                            <Box sx={{ height: 160, mt: 1 }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <LineChart
                                  data={tempPrev.data}
                                  margin={{ top: 8, right: 12, bottom: 4, left: -18 }}
                                >
                                  <CartesianGrid stroke={chartChrome.grid} strokeDasharray="2 4" vertical={false} />
                                  <XAxis
                                    dataKey="t"
                                    tick={{ fontSize: 11, fill: chartChrome.tick }}
                                    tickLine={false}
                                    axisLine={{ stroke: chartChrome.axisLine }}
                                    unit="°C"
                                  />
                                  <YAxis
                                    domain={[tempPrev.yMin, 101]}
                                    tick={{ fontSize: 11, fill: chartChrome.tick }}
                                    tickLine={false}
                                    axisLine={false}
                                    unit="%"
                                  />
                                  <RechartsTooltip
                                    {...rechartsTooltipProps}
                                    formatter={(value) => [tr(`${value}% of nominal RTE`), tr("Efficiency factor")]}
                                    labelFormatter={(label) => tr(`Ambient ${label} °C (worst hour)`)}
                                  />
                                  <Line
                                    type="linear"
                                    dataKey="factor"
                                    name={tr("RTE factor")}
                                    stroke={previewColors.main}
                                    strokeWidth={2}
                                    dot={false}
                                  />
                                </LineChart>
                              </ResponsiveContainer>
                            </Box>
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                              {tempPrev.custom
                                ? "Datasheet curve in use — the dispatch applies these points on each day's worst hour."
                                : "Standard curve: at −10 °C a round trip keeps 94% of its nominal efficiency, at 0 °C 97%; between 10 and 35 °C there is no effect."}
                            </Typography>
                          </>
                        );
                      })()}
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    {(() => {
                    // A rate of zero and an empty cell were the same thing, and
                    // both meant "not modelled" without ever saying so. The
                    // switch says it, and switching it on loads the typical
                    // figure so that "on" charges something.
                    const sdStored = bpCfg.selfDischargeEnabled;
                    const sdOn =
                      sdStored === undefined || sdStored === null || sdStored === ""
                        ? Number(bpCfg.selfDischargePctPerMonth) > 0
                        : Boolean(sdStored);
                    const setSdOn = (on) => {
                      let nextCfg = setPathInObject(
                        cfg, "batteryPerformance.selfDischargeEnabled", on);
                      nextCfg = setPathInObject(
                        nextCfg, "batteryPerformance.selfDischargePctPerMonth",
                        on ? (Number(bpCfg.selfDischargePctPerMonth) > 0
                          ? bpCfg.selfDischargePctPerMonth
                          : SELF_DISCHARGE_TYPICAL_PCT_MONTH) : "");
                      commitCfg(nextCfg);
                    };
                    return (
                      <>
                        <LabeledSwitchWithHint
                          label="Model self-discharge"
                          checked={sdOn}
                          onChange={(e) => setSdOn(e.target.checked)}
                          hint="Stored energy decays while the battery sits idle. It matters for strategies that hold charge for days; for a battery cycling daily the effect is small. If the datasheet figure already includes the BMS standby draw, model that part under Auxiliary Loads instead — do not count it twice."
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", display: "block", ml: 0.5, mt: -0.5, mb: sdOn ? 1.5 : 0 }}
                        >
                          {sdOn
                            ? "Started from the typical Li-ion figure — replace it with the datasheet value."
                            : "Off: stored energy is held indefinitely at no loss."}
                        </Typography>
                        {sdOn && (
                          <TextField
                            size="small" type="number" label="Self-discharge (%/month)"
                            value={bpCfg.selfDischargePctPerMonth ?? ""}
                            onChange={(e) => numField("selfDischargePctPerMonth", e.target.value)}
                            helperText="Typical Li-ion 1–3%."
                            inputProps={{ min: 0, max: 50, step: 0.5 }}
                            sx={{ maxWidth: 280 }}
                            fullWidth
                          />
                        )}
                      </>
                    );
                    })()}
                    {(() => {
                      const sd = buildSelfDischargePreview(bpCfg.selfDischargePctPerMonth);
                      if (!sd) return null;
                      return (
                        <>
                          <Box sx={{ height: 160, mt: 1 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart
                                data={sd.data}
                                margin={{ top: 8, right: 12, bottom: 4, left: -18 }}
                              >
                                <CartesianGrid stroke={chartChrome.grid} strokeDasharray="2 4" vertical={false} />
                                <XAxis
                                  dataKey="day"
                                  tick={{ fontSize: 11, fill: chartChrome.tick }}
                                  tickLine={false}
                                  axisLine={{ stroke: chartChrome.axisLine }}
                                  label={{ value: tr("days idle"), position: "insideBottomRight", dy: 8, fontSize: 10, fill: chartChrome.tickFaint }}
                                />
                                <YAxis
                                  domain={[Math.max(0, Math.floor(sd.data[sd.data.length - 1].soc) - 2), 101]}
                                  tick={{ fontSize: 11, fill: chartChrome.tick }}
                                  tickLine={false}
                                  axisLine={false}
                                  unit="%"
                                />
                                <RechartsTooltip
                                  {...rechartsTooltipProps}
                                  formatter={(value) => [tr(`${value}% of charge kept`), tr("State of charge")]}
                                  labelFormatter={(label) => tr(`${label} days idle`)}
                                />
                                <Line
                                  type="monotone"
                                  dataKey="soc"
                                  name={tr("Charge kept")}
                                  stroke={previewColors.main}
                                  strokeWidth={2}
                                  dot={false}
                                />
                              </LineChart>
                            </ResponsiveContainer>
                          </Box>
                          <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                            A full battery parked idle keeps ≈{sd.keep30}% of its charge after one
                            month — the optimization sees this and avoids holding energy without
                            reason.
                          </Typography>
                        </>
                      );
                    })()}
                  </Grid>
                  </Grid>
                  </SubSection>

                  {/* Only where reserve capacity is actually sold. With none
                      reserved the controls cannot change a number in the study,
                      and the section's own copy talked about "the reserved MW"
                      as though some existed. A setting that can have no effect
                      does not belong on the page. Still shown when the switch is
                      on, so a state someone set is never hidden from them. */}
                  {(afrrReservedMw > 0 || Boolean(bpCfg.afrrEnergyCompliance)) && (
                  <SubSection
                    title="Reserve Market Compliance — aFRR"
                    description="A contractual obligation, not a physical effect: when reserve capacity is sold, the battery must hold enough state of charge to deliver the committed band for the contractual duration. The reserved capacity itself is set in Market Operations."
                  >
                  <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Box>
                      <LabeledSwitchWithHint
                        label="aFRR Energy Compliance"
                        checked={Boolean(bpCfg.afrrEnergyCompliance)}
                        onChange={(e) => setBp("afrrEnergyCompliance", e.target.checked)}
                        hint="When reserve capacity is sold, hold enough state of charge to deliver the upward band and enough headroom to absorb the downward band for the activation duration — the binding constraint of a reserve product."
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block", ml: 0.5, mt: -0.5 }}
                      >
                        {Boolean(bpCfg.afrrEnergyCompliance)
                          ? afrrReservedMw > 0
                            ? `Reserving energy for the committed ${afrrReservedMw} MW band over the duration set beside it.`
                            : "No reserve capacity is sold in this run, so this setting changes nothing. It is shown because it is switched on."
                          : `${afrrReservedMw} MW reserved: power is derated, but the energy to deliver it is not held. Switch on to enforce it.`}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth size="small" type="number" label="aFRR Activation Duration (h)"
                      value={bpCfg.afrrActivationDurationH ?? 1}
                      onChange={(e) => numField("afrrActivationDurationH", e.target.value)}
                      disabled={!bpCfg.afrrEnergyCompliance}
                      helperText="Contractual delivery duration of the committed band"
                      inputProps={{ min: 0.25, max: 4, step: 0.25 }}
                    />
                  </Grid>
                  </Grid>
                  </SubSection>
                  )}

                  {/* Behind the meter only: the price the dispatch pays for the
                      cycle life it uses, derived from this battery's price, cycle
                      life and SoC window (stored as btm.wearCost, as before). */}
                  {isBtm && (
                    <SubSection
                      title="Battery wear in the dispatch"
                      description={WEAR_COST_HELPER_TEXT}
                    >
                      <BtmWearCostField
                        cfg={cfg}
                        onChange={(next) => commitCfg(setPathInObject(cfg, "btm.wearCost", next))}
                      />
                    </SubSection>
                  )}

                  <SubSection
                    title="Battery Augmentation"
                    description="How a mid-life capacity top-up is paid for and when. The capacity is only restored in the study where the outlay that buys it is booked, so the revenue step and the cash outflow always fall in the same year."
                  >
                  {(() => {
                  const augMode = bpCfg.augmentationMode ?? "manual";
                  const augCost = Number(augFin.augmentationCostEur) || 0;
                  const augYearManual = Number(augFin.augmentationYear) || 0;
                  const augFunded = augCost > 0;
                  const augPrev = buildRetentionPreview(bpCfg, projectYearsNow, {
                    cost: augCost, manualYear: augYearManual,
                  });
                  const derivedYear = augPrev
                    ? augPrev.augYear || augPrev.augYearUnfunded
                    : null;
                  // A top-up buys back the capacity that was lost by then:
                  // shortfall x a reference cell price, both shown to the user.
                  const packKwh =
                    (Number(activeBattery?.energy) || 0) * (Number(activePackCount) || 1);
                  const shortfallYear = augMode === "trigger" ? derivedYear : augYearManual;
                  const shortfallKwh =
                    augPrev && shortfallYear && packKwh > 0
                      ? packKwh * (1 - augPrev.retentionAt(shortfallYear))
                      : 0;
                  const estimateEur = Math.round(shortfallKwh * AUGMENTATION_REF_EUR_PER_KWH);
                  return (
                  <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      select fullWidth size="small" label="Augmentation Mode"
                      value={augMode}
                      onChange={(e) => setBp("augmentationMode", e.target.value)}
                      helperText={
                        augMode === "trigger"
                          ? "The year comes from the retention curve; the outlay is set beside it."
                          : augMode === "fom_pct"
                          ? "An annual allowance inside fixed O&M keeps capacity at nameplate."
                          : "A single top-up in a year you choose."
                      }
                    >
                      {/* Short labels for the same reason as the converter list:
                          the menu takes its width from the longest option and
                          overhangs the field. The sentence for each lives in the
                          helper text, which has the width for it. */}
                      <MenuItem value="manual">One-off top-up</MenuItem>
                      <MenuItem value="trigger">Triggered by retention</MenuItem>
                      <MenuItem value="fom_pct">Continuous allowance</MenuItem>
                    </TextField>
                  </Grid>

                  {augMode === "trigger" && (
                    <>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth size="small" type="number" label="Augmentation Trigger (%)"
                          value={bpCfg.augmentationTriggerPct ?? 80}
                          onChange={(e) => numField("augmentationTriggerPct", e.target.value)}
                          helperText={
                            triggerBelowEol
                              ? `Trigger ${bpCfg.augmentationTriggerPct ?? 80}% is at or below the End-of-Life SOH (${eolSohPct}%, Battery Details) — the top-up would coincide with end of life; consider a higher trigger`
                              : "Top up when retention falls below this share of initial capacity"
                          }
                          sx={
                            triggerBelowEol
                              ? { "& .MuiFormHelperText-root": { color: "warning.main" } }
                              : undefined
                          }
                          inputProps={{ min: 50, max: 99, step: 1 }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <TextField
                          fullWidth size="small" label="Year of the top-up"
                          value={tr(derivedYear ? `Year ${derivedYear}` : "Not reached")}
                          InputProps={{ readOnly: true }}
                          // Read-only, and it should look it. An outlined field
                          // identical to the editable ones beside it invites the
                          // click it will not answer.
                          sx={{
                            ...readOnlyFieldSx,
                          }}
                          helperText={
                            derivedYear
                              ? "Derived from the retention curve — not editable"
                              : "The curve never falls below the trigger"
                          }
                        />
                      </Grid>
                    </>
                  )}

                  {augMode === "manual" && (
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        fullWidth size="small" type="number" label="Augmentation Year"
                        value={augFin.augmentationYear ?? ""}
                        onChange={(e) => setFinancial("augmentationYear", e.target.value)}
                        helperText={`Project year of the top-up (1–${projectYearsNow})`}
                        inputProps={{ min: 0, max: projectYearsNow, step: 1 }}
                      />
                    </Grid>
                  )}

                  {/* Its own row, below the three that decide WHEN: this is the
                      one that decides WHETHER anything happens at all, and its
                      warning needs the width to be read. */}
                  {augMode !== "fom_pct" && (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth size="small" type="number" required
                        label="Augmentation Cost (€)"
                        value={augFin.augmentationCostEur ?? ""}
                        onChange={(e) => setFinancial("augmentationCostEur", e.target.value)}
                        helperText={
                          augFunded
                            ? "Booked as a CAPEX outflow in that year; capacity is restored from then on."
                            : "Required — without an outlay no capacity is restored, because nothing paid for it."
                        }
                        sx={
                          augFunded
                            ? undefined
                            : { "& .MuiFormHelperText-root": { color: "warning.dark", fontWeight: 500 } }
                        }
                        inputProps={{ min: 0, step: 1000 }}
                      />
                      {!augFunded && estimateEur > 0 && (
                        <Button
                          size="small"
                          onClick={() => setFinancial("augmentationCostEur", String(estimateEur))}
                          sx={{ textTransform: "none", mt: 0.25 }}
                        >
                          Use {formatEurCompact(estimateEur)} — {Math.round(shortfallKwh).toLocaleString("en-US")} kWh
                          shortfall at {AUGMENTATION_REF_EUR_PER_KWH} €/kWh
                        </Button>
                      )}
                    </Grid>
                  )}

                  {augMode !== "fom_pct" && (() => {
                    const reserveOn = Boolean(bpCfg.augmentationReserveEnabled);
                    const reserveYearNow = augMode === "trigger" ? derivedYear : augYearManual;
                    const reserveYears = Math.max(0, (Number(reserveYearNow) || 0) - 1);
                    const reserveAnnual =
                      reserveYears > 0 && augCost > 0 ? augCost / reserveYears : 0;
                    return (
                      <Grid size={{ xs: 12, md: 5 }}>
                        <LabeledSwitchWithHint
                          label="Pre-fund through a maintenance reserve"
                          checked={reserveOn}
                          onChange={(e) => setBp("augmentationReserveEnabled", e.target.checked)}
                          hint="Instead of the whole outlay hitting the cash flow in the top-up year — which makes that year the weakest of every coverage test — an equal amount is set aside in each year before it and the top-up is paid from the accumulated balance. Standard lender practice for foreseeable mid-life expenditure. Only the timing of the cash changes; the top-up, its year and the total spent stay the same."
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", display: "block", ml: 0.5, mt: -0.5, fontSize: "0.78rem", lineHeight: 1.5 }}
                        >
                          {reserveOn
                            ? reserveYears > 0 && reserveAnnual > 0
                              ? `€${Math.round(reserveAnnual).toLocaleString("en-US")} set aside in each of years 1–${reserveYears}, drawn in year ${reserveYearNow}. Coverage carries the cost evenly instead of dipping once.`
                              : reserveYearNow && Number(reserveYearNow) <= 1
                              ? "The top-up falls in year 1, so there are no earlier years to accumulate in — the outlay is booked in year 1 as usual."
                              : "Set the top-up year and cost to see the annual contribution."
                            : "Off: the whole outlay is booked in the top-up year, and that year's coverage dip is shown in the debt section."}
                        </Typography>
                      </Grid>
                    );
                  })()}

                  {augMode === "fom_pct" && (() => {
                    // The allowance is the battery's WHOLE fixed O&M under the
                    // ATB convention, so the maintenance already budgeted under
                    // Economics sits inside it. Show that here, at the moment of
                    // setting it, rather than only in the report.
                    const inv = cfg?.feasibility?.economics?.investmentCosts || {};
                    const allowancePct = Number(bpCfg.augmentationFomPctYear ?? 2.5) || 0;
                    // Simplified costing (Economics): the battery system turnkey
                    // at €/kWh x kWh, with no separate integration or soft costs.
                    const simplifiedCosting = String(inv.costingMode || "").trim().toLowerCase() === "simplified";
                    const enteredBatteryEur = Number(cfg?.feasibility?.economics?.equipmentCostOverride?.battery);
                    const batteryOmPct = Number(simplifiedCosting ? inv.simpleBatteryOmPctYear : inv.batteryRecurringPctYear) || 0;
                    const integrationEurPerWh = simplifiedCosting ? 0 : Number(inv.batteryEurPerWhdc) || 0;
                    const softUp = simplifiedCosting ? 1 : 1 + (Number(inv.laborPermitsPctSubtotal) || 0) / 100;
                    const contUp = simplifiedCosting ? 1 : 1 + (Number(inv.contingencyPctSubtotal) || 0) / 100;
                    const equipmentEur = simplifiedCosting
                      ? (String(cfg?.feasibility?.economics?.equipmentCostOverride?.battery ?? "").trim() !== "" && Number.isFinite(enteredBatteryEur)
                        ? enteredBatteryEur : (Number(inv.simpleBatteryEurPerKwh) || 0) * packKwh)
                      : Number(price) > 0 ? Number(price) * packCount : 0;
                    const integrationEur = integrationEurPerWh * 1000 * packKwh;
                    const installedEur = equipmentEur + integrationEur * softUp * contUp;
                    const knowCosts = installedEur > 0;
                    const targetEur = allowancePct * 1e-2 * installedEur;
                    // Battery O&M is a % of the battery price (the engine's
                    // battery_amc_rule), like PV and inverter O&M.
                    const alreadyEur = batteryOmPct * 1e-2 * equipmentEur;
                    const addedEur = Math.max(0, targetEur - alreadyEur);
                    const eur = (v) => `€${Math.round(v).toLocaleString("en-US")}`;
                    return (
                      <>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            fullWidth size="small" type="number"
                            label="Annual Allowance (% of installed battery cost / yr)"
                            value={bpCfg.augmentationFomPctYear ?? 2.5}
                            onChange={(e) => numField("augmentationFomPctYear", e.target.value)}
                            helperText="2.5%/yr keeps a battery at nameplate over 15 years (NREL Annual Technology Baseline). Charged every year inside fixed O&M, so no separate outlay is needed."
                            inputProps={{ min: 0, max: 10, step: 0.1 }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 8 }}>
                          <Box
                            sx={{
                              ...previewSurfaceSx, px: 1.5, py: 1.25,
                            }}
                          >
                            <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", display: "block" }}>
                              This percentage is the battery&apos;s whole fixed O&amp;M
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", display: "block", mt: 0.25, fontSize: "0.78rem", lineHeight: 1.5 }}
                            >
                              Under this convention the allowance covers the top-ups <i>and</i> the
                              ordinary battery maintenance. Whatever is entered as
                              <b> Battery ({batteryOmPct || 0}%/Year)</b> under Economics &rarr;
                              Investment Costs is therefore counted inside it, never charged on
                              top{batteryOmPct > 0 ? "" : " — nothing is entered there at present"}.
                            </Typography>
                            {knowCosts ? (
                              <Box sx={{ display: "flex", gap: 2.5, mt: 1, flexWrap: "wrap" }}>
                                <Box>
                                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main", lineHeight: 1.15 }}>
                                    {eur(targetEur)}/yr
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    total allowance
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", lineHeight: 1.15 }}>
                                    {eur(alreadyEur)}/yr
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    already budgeted
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "primary.main", lineHeight: 1.15 }}>
                                    {eur(addedEur)}/yr
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    added by this setting
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: "text.primary", lineHeight: 1.15 }}>
                                    {eur(installedEur)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    installed battery cost
                                  </Typography>
                                </Box>
                              </Box>
                            ) : (
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary", display: "block", mt: 0.75, fontStyle: "italic" }}
                              >
                                Select a battery and set its integration cost under Economics to
                                see the amounts in euro.
                              </Typography>
                            )}
                            {knowCosts && addedEur <= 0 && (
                              <Typography
                                variant="caption"
                                sx={{ color: "warning.dark", fontWeight: 500, display: "block", mt: 0.75, fontSize: "0.78rem" }}
                              >
                                The maintenance already budgeted meets the allowance on its own,
                                so this setting adds nothing further.
                              </Typography>
                            )}
                          </Box>
                        </Grid>
                      </>
                    );
                  })()}

                  </Grid>
                  );
                  })()}
                  </SubSection>

                  <SubSection
                    title="Capacity-Path Re-dispatch"
                    description="A battery that has lost capacity does not simply earn proportionally less: with a smaller usable window the optimizer keeps the best remaining price spreads, so revenue falls more slowly than capacity. Re-optimizing selected project years measures that effect instead of assuming it. This is the capacity axis; the separate price axis lives under Economics, and the two combine — they are not alternatives."
                  >
                  {(() => {
                    const horizonYears = Math.max(1, Number(projectYearsNow) || 20);
                    const parseAnchorYears = (raw) => {
                      const out = new Set();
                      String(raw ?? "")
                        .replace(/;/g, ",")
                        .split(",")
                        .forEach((token) => {
                          const y = Math.round(Number(String(token).trim()));
                          if (Number.isFinite(y) && y >= 2 && y <= horizonYears) out.add(y);
                        });
                      return [...out].sort((a, b) => a - b);
                    };
                    // Year 1 is the base optimization itself, so it is never an
                    // anchor. The final year is always included: it is the year
                    // the coverage ratios are weakest and lenders look at first.
                    const representativeYears = (() => {
                      const set = new Set(
                        [5, 10, 15, 20, 25, 30].filter((y) => y >= 2 && y < horizonYears));
                      if (horizonYears >= 2) set.add(horizonYears);
                      return [...set].sort((a, b) => a - b);
                    })();
                    const selectedYears = parseAnchorYears(bpCfg.multiYearAnchors);
                    const storedMode = String(bpCfg.multiYearRedispatchMode ?? "").trim().toLowerCase();
                    const redispatchMode = ["off", "representative", "custom"].includes(storedMode)
                      ? storedMode
                      : selectedYears.length
                      ? "custom"
                      : "off";
                    const degradationOff = (bpCfg.degradationMode ?? "annual_pct") === "legacy";
                    const allowanceHoldsCapacity = (bpCfg.augmentationMode ?? "manual") === "fom_pct";
                    const blockedReason = degradationOff
                      ? "A battery degradation basis is needed first — without a capacity path there is no degraded year to re-optimize. Set one under Battery Degradation."
                      : allowanceHoldsCapacity
                      ? "The continuous allowance keeps capacity at nameplate every year, so there is no degraded year to re-optimize. Choose a one-off or triggered top-up to use this."
                      : null;
                    const writeYears = (nextCfg, years) =>
                      setPathInObject(nextCfg, "batteryPerformance.multiYearAnchors", years.join(", "));
                    const setRedispatchMode = (next) => {
                      let nextCfg = setPathInObject(
                        cfg, "batteryPerformance.multiYearRedispatchMode", next);
                      if (next === "off") nextCfg = writeYears(nextCfg, []);
                      else if (next === "representative") nextCfg = writeYears(nextCfg, representativeYears);
                      else if (!selectedYears.length) nextCfg = writeYears(nextCfg, representativeYears);
                      commitCfg(nextCfg);
                    };
                    const toggleYear = (year) => {
                      const next = selectedYears.includes(year)
                        ? selectedYears.filter((y) => y !== year)
                        : [...selectedYears, year].sort((a, b) => a - b);
                      commitCfg(writeYears(cfg, next));
                    };
                    const effectiveYears = redispatchMode === "off" ? [] : selectedYears;

                    // --- Augmentation-step anchors, resolved ONCE ------------------
                    // The engine appends these to the capacity list BEFORE any pass
                    // counter reads it, so every number shown in this section has to
                    // include them. Computing them here rather than inside the switch
                    // below is the whole point: the first build resolved them only in
                    // the switch, and the merged list above it then advertised 5 runs
                    // for a project the engine ran with 6.
                    const augModeNow = bpCfg.augmentationMode ?? "manual";
                    const augCostNow = Number(augFin.augmentationCostEur) || 0;
                    const augYearManualNow = Math.round(Number(augFin.augmentationYear) || 0);
                    // An UNFUNDED crossing restores nothing and the continuous
                    // allowance never steps, so neither produces a step to bracket.
                    const augStepFunded = augCostNow > 0 && augModeNow !== "fom_pct";
                    // Mirrors the engine's bool(src.get(key, True)) exactly: ABSENT
                    // means on, so a project saved before this key existed still gets
                    // the correction; an explicit false stays off. Boolean(undefined)
                    // would render off while the engine added the years.
                    const _troughRaw = bpCfg.anchorAugmentationTrough;
                    const troughOn =
                      _troughRaw === undefined ? true : Boolean(_troughRaw);
                    // Under the physics mode the retention curve is only generated
                    // during the run, so the trigger year cannot be promised here -
                    // and an anchor on the wrong side of the step is worse than no
                    // anchor, which is why the engine also declines to guess it.
                    const troughYearUnknown =
                      augStepFunded && augModeNow === "trigger"
                      && (bpCfg.degradationMode ?? "annual_pct") === "pysam_derived";
                    const augPrevNow = augStepFunded
                      ? buildRetentionPreview(bpCfg, horizonYears, {
                          cost: augCostNow, manualYear: augYearManualNow,
                        })
                      : null;
                    const augRestartYear = !augStepFunded || troughYearUnknown
                      ? null
                      : augModeNow === "trigger"
                      ? (augPrevNow ? augPrevNow.augYear : null)
                      : augYearManualNow >= 1 && augYearManualNow <= horizonYears
                      ? augYearManualNow
                      : null;
                    // Year 1 is the base optimization, so it is never an anchor.
                    const troughYears = augRestartYear
                      ? [augRestartYear - 1, augRestartYear].filter(
                          (y) => y > 1 && y <= horizonYears)
                      : [];
                    const troughAddedYears =
                      troughOn && !blockedReason && redispatchMode !== "off"
                        ? troughYears.filter((y) => !effectiveYears.includes(y))
                        : [];
                    // effectiveYears  = what the operator PICKED.
                    // capacityYearsRun = what the ENGINE will dispatch.
                    const capacityYearsRun =
                      [...new Set([...effectiveYears, ...troughAddedYears])]
                        .sort((a, b) => a - b);

                    // Unified re-dispatch (Economics -> forward price curve). When it
                    // is on the price anchors and these capacity anchors are one list,
                    // so the count shown here has to be the merged one - otherwise the
                    // form promises a runtime the engine will not spend.
                    const mrpCfg = cfg?.merchantRevenueProjection || {};
                    const unifiedRedispatchOn =
                      String(mrpCfg.mode || "") === "forward_curve_redispatch"
                      && Boolean(mrpCfg.unifiedRedispatch);
                    const priceYears = unifiedRedispatchOn
                      ? forwardRedispatchYears(
                          horizonYears, (mrpCfg.forwardCurve || {}).redispatchGranularity)
                      : [];
                    const mergedRedispatchYears = unifiedRedispatchOn
                      ? unifiedRedispatchYears(
                          horizonYears, (mrpCfg.forwardCurve || {}).redispatchGranularity,
                          capacityYearsRun)
                      : [];
                    const splitRedispatchPasses =
                      priceYears.length + capacityYearsRun.length;
                    return (
                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 4 }}>
                          <TextField
                            select fullWidth size="small" label="Re-dispatch at Degraded Capacity"
                            value={blockedReason ? "off" : redispatchMode}
                            disabled={Boolean(blockedReason)}
                            onChange={(e) => setRedispatchMode(e.target.value)}
                            helperText={
                              blockedReason
                                ? "Not available with the current degradation settings."
                                : redispatchMode === "off"
                                ? "The year-1 result is scaled by the remaining capacity."
                                : redispatchMode === "representative"
                                ? "Years chosen to span the capacity path, including the final year."
                                : "Pick the years to re-optimize."
                            }
                          >
                            <MenuItem value="off">Not modelled</MenuItem>
                            <MenuItem value="representative">Representative years</MenuItem>
                            <MenuItem value="custom">Chosen years</MenuItem>
                          </TextField>
                        </Grid>

                        {blockedReason && (
                          <Grid size={{ xs: 12, md: 8 }}>
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", display: "block", fontSize: "0.78rem", lineHeight: 1.5 }}
                            >
                              {blockedReason}
                            </Typography>
                          </Grid>
                        )}

                        {!blockedReason && redispatchMode !== "off" && (
                          <Grid size={{ xs: 12, md: 8 }}>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: "text.secondary", display: "block", mb: 0.75 }}
                            >
                              {redispatchMode === "custom"
                                ? `Project years to re-optimize (of ${horizonYears})`
                                : `Project years to re-optimize — years ${effectiveYears.join(", ")} of ${horizonYears}`}
                            </Typography>
                            {redispatchMode === "custom" && (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {Array.from({ length: horizonYears - 1 }, (_, i) => i + 2).map((year) => (
                                  <Chip
                                    key={year}
                                    label={year}
                                    size="small"
                                    color={selectedYears.includes(year) ? "primary" : "default"}
                                    variant={selectedYears.includes(year) ? "filled" : "outlined"}
                                    onClick={() => toggleYear(year)}
                                    sx={{ minWidth: 38, fontWeight: 600 }}
                                  />
                                ))}
                              </Box>
                            )}
                            <Typography
                              variant="caption"
                              sx={{ color: effectiveYears.length > 6 ? "warning.dark" : "text.secondary",
                                    display: "block", mt: 0.75, fontSize: "0.78rem", lineHeight: 1.5 }}
                            >
                              {effectiveYears.length === 0
                                ? "No year selected — the first year is scaled, as if re-dispatch were off."
                                : `${effectiveYears.length} extra full-year optimization${
                                    effectiveYears.length === 1 ? "" : "s"
                                  } — about ${(effectiveYears.length * 365).toLocaleString("en-GB")} more daily solves — will run at that year's capacity and the current price shape; the years in between are interpolated. For a hybrid the PV series is aged alongside the battery, so the result is a whole-plant factor.${
                                    effectiveYears.length > 6 ? " This many years will noticeably lengthen the run." : ""
                                  }`}
                            </Typography>
                            {/* Two axes. Whether they cost two sets of runs or
                                one depends on the unified switch under
                                Economics, and so does whether the two results
                                are multiplied - so the text has to say which
                                world this project is in rather than describe
                                one of them. */}
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", display: "block", mt: 0.5,
                                    fontSize: "0.78rem", lineHeight: 1.5 }}
                            >
                              {unifiedRedispatchOn ? (
                                <>
                                  These capacity years are MERGED with the price years from the
                                  forward curve under Economics: {mergedRedispatchYears.join(", ")}
                                  {" "}— {mergedRedispatchYears.length} full-year run
                                  {mergedRedispatchYears.length === 1 ? "" : "s"} instead of
                                  {" "}{splitRedispatchPasses}. The two lists do not have to match:
                                  a year on only one of them still gets its own run, and a year on
                                  both is solved once. Each run applies that year&apos;s price
                                  level and that year&apos;s capacity together, so the answer is
                                  the year&apos;s revenue and nothing is multiplied on top of it.
                                </>
                              ) : (
                                <>
                                  These are the capacity years. If the merchant projection under
                                  Economics is set to a forward curve, it re-dispatches its own
                                  anchor years for the price level, and those runs are additional
                                  to these — the engine solves both sets and multiplies the two
                                  factors, one for capacity and one for price. Turning on “Solve
                                  price and capacity in the same pass” under Economics merges the
                                  two lists into one set of runs instead.
                                </>
                              )}
                            </Typography>
                            {/* Behind the meter these years are not a merchant
                                nicety: a saving is a bill difference, and whether
                                capacity fade eats into the demand-charge reduction
                                depends on whether this site's billing peak is short
                                (power-limited, so the kW cut holds) or a broad plateau
                                (energy-limited, so it fades). Re-dispatching answers
                                that from the load profile instead of assuming it. */}
                            <Typography
                              variant="caption"
                              sx={{ color: "text.secondary", display: "block", mt: 0.5,
                                    fontSize: "0.78rem", lineHeight: 1.5 }}
                            >
                              For a behind-the-meter study the same years re-dispatch the
                              SAVINGS: the engine measures how much of the peak-shaving
                              reduction still survives at the degraded capacity on this
                              site&apos;s own load profile, rather than assuming the
                              demand-charge saving holds. Without a year selected here,
                              the savings schedule falls back to one annual factor.
                            </Typography>
                          </Grid>
                        )}

                        {/* The capacity path is not smooth. A funded top-up
                            restores capacity in a single year, so retention
                            steps UP there. Linear interpolation between an
                            anchor before the step and one after it climbs
                            across that discontinuity, which overstates the
                            years just before the top-up. Measured on the
                            reference 11A project: year 7 came out 1.7% high
                            and the NPV differed by 2.1% between two anchor
                            sets on otherwise identical inputs. Only offered
                            when a funded step actually exists - with no step
                            there is nothing to anchor around. */}
                        {!blockedReason && redispatchMode !== "off" && (() => {
                          // Everything here was resolved ONCE further up, so this
                          // caption and the merged-list paragraph above cannot state
                          // different pass counts for the same project.
                          if (!augStepFunded) return null;
                          const yearUnknown = troughYearUnknown;
                          const restartYear = augRestartYear;
                          // troughAddedYears is gated on troughOn; the caption also
                          // describes the OFF state, so recompute the "what would be
                          // added" set here independently of the switch position.
                          const addedYears = troughYears.filter(
                            (y) => !effectiveYears.includes(y));
                          const merged = [...new Set([...effectiveYears, ...troughYears])]
                            .sort((a, b) => a - b);
                          return (
                            <Grid size={{ xs: 12 }}>
                              <LabeledSwitchWithHint
                                label="Also re-optimize around the augmentation step"
                                checked={troughOn}
                                onChange={(e) =>
                                  setBp("anchorAugmentationTrough", e.target.checked)}
                                hint="The top-up restores capacity in one year, so the capacity path has a step in it. Between two anchors that straddle that step the schedule is interpolated, and the interpolation climbs across the discontinuity — so the years just before the top-up, when the battery is at its weakest, are credited with revenue it never earns. Adding the top-up year and the year before it puts a measured point on each side, so no interpolation crosses the step. On the reference 11A project this was worth 1.7% in the year before the top-up and 2.1% of NPV — larger than the effect of the unified-pass switch itself."
                              />
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary", display: "block", ml: 0.5,
                                      mt: -0.5, fontSize: "0.78rem", lineHeight: 1.5 }}
                              >
                                {!troughOn
                                  ? yearUnknown
                                    ? "Off: the years above are used as they are. A triggered top-up will restore capacity somewhere on the path, and the schedule will interpolate across that step."
                                    : troughYears.length === 0
                                    ? "Off \u2014 and with the top-up in year 1 there is no earlier year to anchor, so this would change nothing."
                                    : addedYears.length === 0
                                    ? `Off, but years ${troughYears.join(" and ")} are already selected above, so the step in year ${restartYear} is already measured on both sides.`
                                    : `Off: the schedule interpolates across the capacity step in year ${restartYear}, which overstates the years just before it. Turning this on would add year${addedYears.length === 1 ? "" : "s"} ${addedYears.join(" and ")}.`
                                  : yearUnknown
                                  ? "On: the top-up year is derived from the capacity curve during the run, so it cannot be listed here \u2014 the engine adds that year and the one before it once it knows them, and the report states which years were used."
                                  : troughYears.length === 0
                                  ? "On, but the top-up falls in year 1 \u2014 that is the base optimization itself, so there is no earlier year to add."
                                  : addedYears.length === 0
                                  ? `On \u2014 nothing to add: years ${troughYears.join(" and ")} are already selected, so the step in year ${restartYear} is measured on both sides.`
                                  : `On: year${addedYears.length === 1 ? "" : "s"} ${addedYears.join(" and ")} ${addedYears.length === 1 ? "is" : "are"} added automatically \u2014 ${merged.length} capacity year${merged.length === 1 ? "" : "s"} in total (${merged.join(", ")}), one on each side of the top-up in year ${restartYear}. That is ${addedYears.length} extra full-year optimization${addedYears.length === 1 ? "" : "s"}, about ${(addedYears.length * 365).toLocaleString("en-GB")} more daily solves.`}
                              </Typography>
                            </Grid>
                          );
                        })()}
                      </Grid>
                    );
                  })()}
                  </SubSection>

                  <PerformancePreview
                    bpCfg={bpCfg}
                    ratedKw={(Number(activeBattery?.p_max_d) || 0) * (Number(activePackCount) || 1)}
                    projectYears={projectYearsNow}
                    augFunding={{
                      cost: Number(augFin.augmentationCostEur) || 0,
                      manualYear: Number(augFin.augmentationYear) || 0,
                    }}
                  />
                </Stack>
              );
            })()}
          </AccordionDetails>
        </Accordion>
        <BESSKnowledgePanel
          cfg={cfg}
          detailsData={detailsData}
        />
      </Paper>




      {/* Details dialog */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Battery details – <span data-no-translate>{detailsData?.name || detailsTarget?.name || ""}</span>
        </DialogTitle>

        <DialogContent dividers>
          {detailsLoading && <Typography variant="body2">Loading details…</Typography>}

          {detailsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailsError}
            </Alert>
          )}

          {!detailsLoading && !detailsError && detailsData && (
            <BatteryDiagram details={detailsData} />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>


      {/* Removing a battery from the catalogue: the same warning as the PV
          modules and inverters (ConfigForm), since other projects of the
          organisation may name it. */}
      <Dialog
        open={Boolean(batteryDelete)}
        onClose={() => (batteryDeleteBusy ? null : setBatteryDelete(null))}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: (t) => t.zIndex.modal + 10 }}
      >
        <DialogTitle>Remove battery from catalogue?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }} data-no-translate>
            {batteryDelete?.name}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            This will permanently remove the equipment from your organization's catalogue. This action cannot be undone.
          </Typography>
          <Alert severity="warning" sx={{ mb: 1 }}>
            Active projects within your organization may reference this equipment. Simulations for those projects will not be able to run until an alternative is selected.
          </Alert>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            We recommend confirming that no ongoing studies depend on this item before proceeding.
          </Typography>
          {batteryDeleteError ? (
            <Alert severity="error" sx={{ mt: 1.5 }}>
              {batteryDeleteError}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatteryDelete(null)} disabled={batteryDeleteBusy}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={confirmBatteryDelete} disabled={batteryDeleteBusy}>
            {batteryDeleteBusy ? "Removing…" : "Remove permanently"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={catalogWriteAllowed && addOpen} onClose={() => !addSaving && setAddOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add custom battery</DialogTitle>

        <DialogContent dividers>
          {isAdminUser && <CatalogVisibilityFields visibility={batteryVisibility} setVisibility={setBatteryVisibility}
            selected={batteryOrganizations} setSelected={setBatteryOrganizations}
            organizations={catalogOrganizations} loading={catalogOrganizationsLoading}
            error={catalogOrganizationsError} greek={isGreek} />}
          <Grid container spacing={2}>
            {/* Battery name FIRST (manual, required) */}
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Battery name *"
                value={newBattery.battery_name}
                error={Boolean(errors.battery_name)}
                helperText={errors.battery_name}
                  InputLabelProps={{
                    sx: {
                      color: "primary.main",           // normal state
                      fontWeight: 600,
                      letterSpacing: "0.02em",

                      "&.Mui-focused": {
                        color: "primary.main",         // when focused
                      },
                    },
                  }}
                onChange={(e) =>
                  setNewBattery((p) => ({ ...p, battery_name: e.target.value }))
                }
              />
            </Grid>

            {/* 🔽 ADD THE MAP HERE */}
            {BATTERY_FIELDS.map((f) => {
              const value = newBattery[f.key];
              const errMsg = errors[f.key] || "";

              const commonProps = {
                fullWidth: true,
                label: tr(f.label) + (f.required ? " *" : ""),
                value: value,
                error: Boolean(errMsg),
                helperText: errMsg || f.helper,
              };

              // NUMBER FIELD
              if (f.kind === "number") {
                return (
                  <Grid size={{ xs: 12, md: 3 }} key={f.key}>
                    <TextField
                      {...commonProps}
                      inputMode="decimal"
                      type="text"
                      onChange={(e) => {
                        const raw = e.target.value.replace(",", ".");
                        if (!NUM_RE.test(raw)) return;

                        setNewBattery((p) => {
                          const next = { ...p, [f.key]: raw };
                          const msg = validateField(f, raw, next);
                          setErrors((prev) => ({ ...prev, [f.key]: msg || undefined }));
                          return next;
                        });
                      }}
                      onBlur={() => {
                        setErrors((prev) => {
                          const msg = validateField(f, newBattery[f.key], newBattery);
                          return { ...prev, [f.key]: msg || undefined };
                        });
                      }}
                      InputProps={{
                        endAdornment: f.unit ? (
                          <InputAdornment position="end">{f.unit}</InputAdornment>
                        ) : undefined,
                      }}
                    />
                  </Grid>
                );
              }

              if (f.kind === "select") {
                return (
                  <Grid size={{ xs: 12, md: 3 }} key={f.key}>
                    <TextField
                      {...commonProps}
                      select
                      sx={{ width: "100%", minWidth: 0 }}
                      InputLabelProps={{ shrink: true }}
                      onChange={(e) => {
                        const raw = e.target.value;
                        setNewBattery((p) => {
                          const next = { ...p, [f.key]: raw };
                          const msg = validateField(f, raw, next);
                          setErrors((prev) => ({ ...prev, [f.key]: msg || undefined }));
                          return next;
                        });
                      }}
                    >
                      <MenuItem value="">None</MenuItem>
                      {f.options?.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                );
              }

              // TEXT FIELD
              return (
                <Grid size={{ xs: 12, md: 3 }} key={f.key}>
                  <TextField
                    {...commonProps}
                    type="text"
                    onChange={(e) => {
                      const raw = e.target.value;
                      setNewBattery((p) => {
                        const next = { ...p, [f.key]: raw };
                        const msg = validateField(f, raw, next);
                        setErrors((prev) => ({ ...prev, [f.key]: msg || undefined }));
                        return next;
                      });
                    }}
                  />
                </Grid>
              );
            })}
          </Grid>

          {/* Optional info banner */}
          <Alert severity="info" sx={{ mt: 2 }}>
            Fields marked with <b>*</b> are required. Numeric fields accept numbers only.
          </Alert>
        </DialogContent>


        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={addSaving}>Cancel</Button>
          <Button
            variant="contained"
            disabled={addSaving || !newBattery.battery_name || !catalogWriteAllowed}
            onClick={async () => {
              if (!catalogWriteAllowed) {
                setToast({ open: true, msg: catalogWriteDisabledMessage, severity: "warning" });
                return;
              }

              if (isAdminUser && batteryVisibility === "selected" &&
                  (batteryOrganizations.length === 0 || catalogOrganizationsError)) {
                setToast({ open: true, msg: isGreek ? "Επιλέξτε τουλάχιστον έναν διαθέσιμο οργανισμό." : "Select at least one available organization.", severity: "error" });
                return;
              }
              const nextErrors = validateAll(newBattery);
              setErrors(nextErrors);

              if (Object.keys(nextErrors).length > 0) {
                setToast({ open: true, msg: "Please fix the highlighted fields.", severity: "error" });
                return;
              }

              setAddSaving(true);
              try {
                // Build characteristics JSON, converting numeric strings to numbers
                const characteristics = { ...newBattery };
                delete characteristics.battery_name;

                const numericKeys = [
                  "Capacity","Charge current","Eff_charge","Eff_discharge","Energy","Height","I_disch_max",
                  "Length","N_cycles","P_max_c","P_max_d","Power","Price","Vcut","Vmax","Vmin","Vnom",
                  "Weight","Width","c-rate_c","c-rate_d","soc_max","soc_min"
                ];

                for (const k of numericKeys) {
                  if (characteristics[k] !== "" && characteristics[k] != null) {
                    characteristics[k] = Number(characteristics[k]);
                  } else {
                    delete characteristics[k]; // optional: remove empties
                  }
                }

                // strings: Client, Vendor, Technology etc remain
                const payload = {
                  battery_name: newBattery.battery_name,
                  characteristics,
                  ...(isAdminUser ? { visibility: batteryVisibility, organizations: batteryVisibility === "selected" ? batteryOrganizations : [] } : {}),
                };

                const created = await createBattery(payload, accessToken);
                const savedName = String(created?.name || created?.battery_name || newBattery.battery_name).trim();

                // Ensure user sees it instantly:
                // 1) reload list
                const data = await getBatteries({});
                setBatteryOptions(data || []);

                // 2) set selected to the newly created battery
                handleField("bess.selected", [
                  ...(selectedBatteries || []),
                  {
                    _key: `name:${savedName}`,
                    id: created.id ?? null,
                    name: savedName,
                    count: 1,
                    overrides: { ...DEFAULT_BATTERY_OVERRIDES },
                  }
                ]);
                handleField("bess.activeName", savedName);

                setToast({
                  open: true,
                  msg: formatCatalogUploadSuccessMessage("Battery", created, newBattery.battery_name),
                  severity: "success",
                });
                setAddOpen(false);
              } catch (e) {
                setToast({ open: true, msg: formatApiError(e, "Failed to save battery."), severity: "error" });
              } finally {
                setAddSaving(false);
              }
            }}
          >
            {addSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity || "success"}
          variant="filled"
          sx={{ borderRadius: 2, fontWeight: 700, width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>


    </>
  );
}
